// src/api-client.ts
import jwtDecode from "jwt-decode";
import EventEmitter from "events";
var ApiClient = class _ApiClient {
  config;
  schemaValidators;
  emitter;
  /* Shared token state */
  static tokenLock;
  static tokenUnlock;
  static tokenTimeOut;
  static tokenUseBuffer;
  static callId;
  static debug;
  static apiRouteConfig;
  /**
   * Maps a pair of accountId and refreshToken to a boolean indicating whether the token is valid.
   *
   * The key is formatted as: "<accountId>:<refreshToken>"
   */
  static isRefreshPayloadValidMap = /* @__PURE__ */ new Map();
  constructor(config, schemaValidators46) {
    this.config = config;
    this.schemaValidators = schemaValidators46;
    this.emitter = new EventEmitter();
    _ApiClient.tokenLock = null;
    _ApiClient.tokenUnlock = () => {
    };
    _ApiClient.tokenTimeOut = null;
    _ApiClient.tokenUseBuffer = null;
    _ApiClient.callId = 0;
    _ApiClient.debug = false;
    _ApiClient.apiRouteConfig = null;
  }
  /* ----------------------------------------------
  	Debug Helper Functions
  ----------------------------------------------*/
  log(msg, ...args) {
    if (_ApiClient.debug) {
      console.error(`[API-CLIENT] ${msg}`, ...args);
    }
  }
  /* ----------------------------------------------
  	Wrappers for config getters and setters
   ----------------------------------------------*/
  async setAccessToken(accessToken) {
    this.log(`Setting access token to ${accessToken}`);
    await this.config.setAccessToken(accessToken);
    this.emitter.emit("access-token-updated", accessToken ? jwtDecode(accessToken) : void 0);
    void this.updateApiRouteConfig();
    this.log(`Set access token to ${accessToken}`);
    return;
  }
  async getAccessToken() {
    this.log(`Getting access token`);
    const accessToken = await this.config.getAccessToken();
    this.log(`Got access token ${accessToken}`);
    return accessToken;
  }
  async setRefreshToken(refreshToken) {
    this.log(`Setting refresh token to ${refreshToken}`);
    await this.config.setRefreshToken(refreshToken);
    this.log(`Set refresh token to ${refreshToken}`);
    return;
  }
  async getRefreshToken() {
    this.log(`Getting refresh token`);
    const refreshToken = await this.config.getRefreshToken();
    this.log(`Got refresh token ${refreshToken}`);
    return refreshToken;
  }
  async setAccountId(accountId) {
    this.log(`Setting account id to ${accountId}`);
    await this.config.setAccountId(accountId);
    this.log(`Set account id to ${accountId}`);
    return;
  }
  async getAccountId() {
    this.log(`Getting account id`);
    const accountId = await this.config.getAccountId();
    this.log(`Got account id ${accountId}`);
    return accountId;
  }
  async getLocalRefreshedTime() {
    this.log(`Getting local refreshed time`);
    const timestamp = await this.config.getLocalRefreshedTime();
    this.log(`Got local refreshed time ${timestamp}`);
    return timestamp;
  }
  async setLocalRefreshedTime(timestamp) {
    this.log(`Setting local refreshed time to ${timestamp}`);
    await this.config.setLocalRefreshedTime(timestamp);
    this.log(`Set local refreshed time to ${timestamp}`);
    return;
  }
  /**
   * Use the refresh token to update the access and refresh tokens.
   *
   * If another updateTokens is in progress, a new call will wait for the original to finish.
   */
  async updateTokens() {
    const myId = _ApiClient.callId++;
    this.log(`[Call ${myId}] Updating tokens`);
    if (_ApiClient.tokenLock) {
      this.log(`[Call ${myId}] Waiting for another updateTokens to finish`);
      await _ApiClient.tokenLock;
      this.log(`[Call ${myId}] Another updateTokens finished`);
      return;
    } else {
      this.log(`[Call ${myId}] Setting new token lock`);
      _ApiClient.tokenLock = new Promise((resolve) => {
        _ApiClient.tokenUnlock = () => {
          resolve(true);
          _ApiClient.tokenLock = null;
        };
        _ApiClient.tokenTimeOut = setTimeout(() => {
          resolve(false);
          _ApiClient.tokenLock = null;
        }, 1e4);
      });
    }
    try {
      if (_ApiClient.tokenUseBuffer) {
        this.log(`[Call ${myId}] Waiting for token buffer to finish`);
        await _ApiClient.tokenUseBuffer;
        this.log(`[Call ${myId}] Token buffer finished`);
        return;
      }
      const currentAccountId = await this.getAccountId();
      const currentRefreshToken = await this.getRefreshToken();
      if (!currentAccountId || !currentRefreshToken) {
        this.log(`[Call ${myId}] No accountId or refreshToken, not updating tokens`);
        return;
      }
      const isRefreshPayloadValidKey = `${currentAccountId}:${currentRefreshToken}`;
      const isRefreshPayloadValid = _ApiClient.isRefreshPayloadValidMap.get(isRefreshPayloadValidKey);
      if (isRefreshPayloadValid !== void 0 && !isRefreshPayloadValid) {
        this.log(`[Call ${myId}] Refresh payload is invalid, not updating tokens`);
        return;
      }
      const response = await fetch(`${this.config.verityOrigin}/api/access-control/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountId: currentAccountId,
          refreshToken: currentRefreshToken
        })
      });
      this.emitter.emit("refresh", response);
      this.log(`[Call ${myId}] Response received from refresh`, response);
      if (response.ok) {
        const { accessToken, refreshToken } = await response.json();
        if (typeof accessToken === "string") {
          await this.setAccessToken(accessToken);
        }
        if (typeof refreshToken === "string") {
          await this.setRefreshToken(refreshToken);
        }
        await this.setLocalRefreshedTime(Date.now());
      } else {
        this.log(`[Call ${myId}] Response not ok from refresh`, response);
        _ApiClient.isRefreshPayloadValidMap.set(isRefreshPayloadValidKey, false);
      }
    } catch (e) {
      this.log(`[Call ${myId}] Error updating tokens`, e);
    } finally {
      this.log(`[Call ${myId}] Clearing token timeout`);
      if (_ApiClient.tokenTimeOut) {
        clearTimeout(_ApiClient.tokenTimeOut);
      }
      this.log(`[Call ${myId}] Freeing token lock`);
      _ApiClient.tokenUnlock?.(false);
      this.log(`[Call ${myId}] Creating token buffer`);
      _ApiClient.tokenUseBuffer = new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
          _ApiClient.tokenUseBuffer = null;
        }, 100);
      });
      this.log(`[Call ${myId}] Finished token update`);
    }
  }
  /**
   * Returns the JWT payload of the current access token, if available.
   * @returns {Jwt | undefined} The JWT payload of the current access token, or undefined if not available.
   */
  async getJwt() {
    this.log(`Getting JWT`);
    const currentAccessToken = await this.getAccessToken();
    if (!currentAccessToken) {
      return void 0;
    }
    const jwt = jwtDecode(currentAccessToken);
    this.log(`Got JWT`, jwt);
    return jwt;
  }
  /**
   * Sets the access token to null and ends the current session.
   */
  async resetAccessToken() {
    this.log(`Resetting access token`);
    await this.setAccessToken(void 0);
    await fetch(`${this.config.verityOrigin}/api/verity/session/end`, { credentials: "include" });
    this.emitter.emit("access-token-updated", void 0);
    this.log(`Reset access token`);
  }
  /**
   * Get the authorization header using the current access token
   */
  async generateAuthorizationHeader() {
    this.log(`Generating authorization header`);
    const accessToken = await this.getAccessToken();
    this.log(`Got authorization header`, { Authorization: `Bearer ${accessToken}` });
    return { Authorization: `Bearer ${accessToken}` };
  }
  /**
   * Mutate the fetch parameters to include the authorization header
   */
  async applyAuthenticatedFetchParams(...params) {
    this.log(`Generating authenticated fetch parameters`);
    const [originalInput, originalInit] = params;
    if (!await this.getAccessToken()) {
      return [originalInput, originalInit];
    }
    try {
      const authHeader = await this.generateAuthorizationHeader();
      if (originalInput instanceof Request && !originalInit?.headers) {
        Object.entries(authHeader).forEach(([key, value]) => originalInput.headers.set(key, value));
        this.log(`Generated authenticated fetch parameters`, { originalInput, originalInit, authHeader });
        return [originalInput, originalInit];
      } else {
        const modifiedInit = {
          ...originalInit,
          headers: {
            ...originalInit?.headers,
            ...authHeader
          }
        };
        this.log(`Generated authenticated fetch parameters`, { originalInput, originalInit, authHeader, modifiedInit });
        return [originalInput, modifiedInit];
      }
    } catch (error) {
      this.log(`Failed to generate authenticated fetch parameters`, error);
      return [originalInput, originalInit];
    }
  }
  /**
   * Update the API route config for this instance
   */
  async updateApiRouteConfig() {
    try {
      this.log(`Updating API route config`);
      const response = await fetch(`${this.config.verityOrigin}/api/verity/version/config`, {
        headers: {
          ...await this.generateAuthorizationHeader()
        }
      });
      if (!response.ok) {
        throw new Error("Failed to update API route config");
      }
      const data = await response.json();
      if (typeof data !== "object" || !data) {
        throw new Error("Invalid API route config");
      }
      const routeConfig = data;
      _ApiClient.apiRouteConfig = routeConfig;
      this.log(`Updated API route config`, routeConfig);
    } catch (error) {
      _ApiClient.apiRouteConfig = null;
      this.log(`Failed to update API route config`, error);
    }
  }
  /**
   * Use the API route config for this instance. Mutates the fetch parameters to include the API version header
   */
  applyApiRouteConfig(...params) {
    const [originalInput, originalInit] = params;
    try {
      this.log(`Using API route config`);
      if (!_ApiClient.apiRouteConfig) {
        return [originalInput, originalInit];
      }
      const targetUrl = originalInput instanceof Request ? originalInput.url : originalInput instanceof URL ? originalInput.toString() : originalInput;
      for (const { route, version } of _ApiClient.apiRouteConfig) {
        if (targetUrl.includes(route)) {
          this.log(`Using API route config for route ${route}`);
          if (originalInput instanceof Request && !originalInit?.headers) {
            Object.entries({ "X-Api-Version": version }).forEach(
              ([key, value]) => originalInput.headers.set(key, value)
            );
            this.log(`Generated API route config`, {
              originalInput,
              originalInit,
              version,
              headers: originalInput.headers
            });
            return [originalInput, originalInit];
          } else {
            const modifiedInit = {
              ...originalInit,
              headers: {
                ...originalInit?.headers,
                "X-Api-Version": version
              }
            };
            this.log(`Generated API route config`, {
              originalInput,
              originalInit,
              version,
              headers: modifiedInit.headers
            });
            return [originalInput, modifiedInit];
          }
        }
      }
      this.log(`No API route config found for route ${targetUrl}`);
      return [originalInput, originalInit];
    } catch (error) {
      this.log(`Failed to use API route config`, error);
      return [originalInput, originalInit];
    }
  }
  /**
   * Get the current route version for a specific route
   */
  getRouteVersion(route) {
    if (!_ApiClient.apiRouteConfig) {
      return "v1";
    }
    return _ApiClient.apiRouteConfig.find((config) => route.includes(config.route))?.version ?? "v1";
  }
  /**
   * Apply fetch overrides for this instance. Mutates the fetch parameters to include the authorization header and API version header
   */
  async applyFetchOverrides(...params) {
    const [originalInput, originalInit] = params;
    try {
      const [authInput, authInit] = await this.applyAuthenticatedFetchParams(originalInput, originalInit);
      const [versionedInput, versionedInit] = this.applyApiRouteConfig(authInput, authInit);
      this.log(`Applying fetch overrides`, { input: versionedInput, init: versionedInit });
      return [versionedInput, versionedInit];
    } catch (error) {
      this.log(`Failed to apply fetch overrides`, error);
      return [originalInput, originalInit];
    }
  }
  fetchPromise;
  /**
   * Clones the fetch parameters to prevent Request objects from being reused
   */
  cloneFetchParams(...params) {
    const [input, init] = params;
    if (input instanceof Request) {
      return [new Request(input), init ? { ...init } : void 0];
    }
    return [input, init ? { ...init } : void 0];
  }
  /**
   * Wrapper around fetch that applies overrides
   */
  async fetch(...params) {
    this.log(`Fetching`, params);
    if (_ApiClient.apiRouteConfig === null) {
      await this.updateApiRouteConfig();
    }
    if (this.fetchPromise) {
      await this.fetchPromise;
    }
    this.fetchPromise = (async () => {
      try {
        const response = await fetch(...await this.applyFetchOverrides(...this.cloneFetchParams(...params)));
        this.log(`Fetch response`, response);
        if (response.status === 401 || response.status === 403) {
          this.log(`Token expired, updating tokens`);
          await this.updateTokens();
          this.log(`Token updated, retrying fetch`);
          const [retryInput, retryInit] = await this.applyFetchOverrides(...this.cloneFetchParams(...params));
          return await fetch(retryInput, retryInit);
        }
        return response;
      } finally {
        this.fetchPromise = void 0;
      }
    })();
    return await this.fetchPromise;
  }
  /**
   * Clear all cookies in the current document
   */
  clearCookies() {
    if (document && document.cookie) {
      const cookies = document.cookie.split(";");
      cookies.forEach((cookie) => {
        const [name] = cookie.split("=");
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      });
    }
  }
};

// src/authentication/index.ts
import { authenticationSchemaValidators as schemaValidators } from "@yujacorp/verity-api-contract/schemas/typebox";

// src/errors.ts
import { Value } from "@sinclair/typebox/value";
var BaseApiClientError = class extends Error {
};
var ApiClientError = class extends BaseApiClientError {
  cause;
  constructor(cause) {
    super(`API Client Error: ${cause?.message ?? "unknown"}`);
    this.name = `ApiClientError: ${cause?.name ?? "unknown"}`;
    this.cause = cause;
  }
};
var ApiClientResponseError = class extends BaseApiClientError {
  status;
  originalMessage;
  code;
  details;
  constructor(status, { code, message, details } = {}) {
    super(`API Client Error: ${code} (${status}): ${message}`);
    this.name = `ApiClientError: ${code} (${status}): ${message}`;
    this.status = status;
    this.originalMessage = message;
    this.code = code;
    this.details = details;
  }
};
var ApiClientSchemaError = class extends BaseApiClientError {
  schema;
  value;
  constructor(schema, value) {
    super(`API Client Error: ${JSON.stringify([...Value.Errors(schema.schema, value)], void 0, 4)}`);
    this.name = "ApiClientError: schema";
    this.schema = schema;
    this.value = value;
  }
};
var UnauthenticatedError = class _UnauthenticatedError extends BaseApiClientError {
  static NAME = "UnauthenticatedError";
  constructor() {
    super(_UnauthenticatedError.NAME);
    this.name = _UnauthenticatedError.NAME;
  }
};
async function handleApiError(promise) {
  try {
    const response = await promise;
    if (response.ok) {
      return response;
    } else if (response.status === 401 || response.status === 403) {
      throw new UnauthenticatedError();
    } else {
      const errorMessage = await response.text();
      let parsedDetails = void 0;
      try {
        if (!errorMessage.includes("<!DOCTYPE html>")) {
          parsedDetails = JSON.parse(errorMessage);
        }
      } catch {
        parsedDetails = { message: errorMessage };
      }
      throw new ApiClientResponseError(response.status, parsedDetails);
    }
  } catch (e) {
    if (e instanceof Error) {
      if (e instanceof BaseApiClientError) {
        throw e;
      }
      throw new ApiClientError(e);
    }
  }
  throw new ApiClientError();
}
async function handleApiErrorWithSchema(promise, schemaValidator) {
  const response = await handleApiError(promise);
  const data = await response.json();
  if (schemaValidator.validate(data)) {
    return data;
  }
  throw new ApiClientSchemaError(schemaValidator, data);
}
function withDefaultErrorHandler(instance) {
  return new Proxy(instance, {
    get(target, prop, receiver) {
      const orig = Reflect.get(target, prop, receiver);
      if (typeof orig === "function") {
        return async (...args) => {
          try {
            return await orig.apply(this, args);
          } catch (err) {
            if (err instanceof Error) throw new ApiClientError(err);
            throw new ApiClientError(new Error(`Unknown error: ${JSON.stringify(err)}`));
          }
        };
      }
      return orig;
    }
  });
}

// src/authentication/index.ts
var AuthenticationClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators);
  }
  isAuthenticated = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/version/config`, {
        credentials: "include"
      });
      if (response.ok) {
        return true;
      }
      return false;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccessCode = async (body) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/session/access-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.accessCode.validate(obj)) {
          return obj.code;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccessControlAuthCode = async () => {
    try {
      const accountId = await this.getAccountId();
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/account/${accountId}/auth-code`,
        {
          method: "POST"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.accessControlAuthCode.validate(obj)) {
          return { accountId, authCode: obj.authCode };
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getRefreshTokenFromAuthCode = async (accountId, authCode) => {
    try {
      const response = await fetch(
        `${this.config.verityOrigin}/api/access-control/account/${accountId}/refresh-token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            authCode
          })
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.refreshTokenInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  validateUserFromServer = async (suffix) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/session/validate/user?${suffix}`, {
        credentials: "include"
      });
      if (response.ok && response.status === 200) {
        return true;
      }
      return false;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
  };
  getAccount = async (accountId) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/account/${accountId}`);
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.account.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccounts = ({ email }) => {
    const searchParams = new URLSearchParams();
    if (email) {
      searchParams.set("email", email);
    }
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/access-control/account?${searchParams.toString()}`),
      this.schemaValidators.accounts
    );
  };
  logout = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/session/end`, {
        credentials: "include"
      });
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  logFailedAuth = async (message) => {
    try {
      await super.fetch(`${this.config.verityOrigin}/api/verity/debug/auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message
        })
      });
      return;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
  };
  getAccountPortals = async ({
    accountId,
    searchQuery,
    page,
    size
  }) => {
    try {
      const queryParams = new URLSearchParams();
      if (searchQuery) queryParams.set("searchQuery", searchQuery);
      queryParams.set("page", page.toString());
      queryParams.set("size", size.toString());
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/account/${accountId}/portals?${queryParams.toString()}`,
        {
          method: "GET",
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.getAccountPortals.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccountInstitutes = async ({ page = 0, size = 1e3 }) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.set("page", page.toString());
      queryParams.set("size", size.toString());
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/institute?${queryParams.toString()}`,
        {
          method: "GET",
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.getAccountInstitutes.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  setCurrentuserId = async ({ accountId, userId }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/account/${accountId}/current-user/${userId}`,
        {
          method: "PUT",
          credentials: "include"
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  mapNonLmsUser = async ({
    userUuid,
    courseUuid,
    instituteUuid
  }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/portal/map`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userUuid,
          courseUuid,
          instituteUuid
        })
      });
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/proctoring-session/index.ts
import {
  proctoringSessionSchemaValidators as schemaValidators18
} from "@yujacorp/verity-api-contract/schemas/typebox";

// src/proctoring-session/logger/access-code/index.ts
import { proctoringSessionLoggerAccessCodeSchemaValidators as schemaValidators2 } from "@yujacorp/verity-api-contract/schemas/typebox";
var AccessCodeLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schemaValidators2);
    this.emitter = emitter2;
  }
  async #logAccessCodeEvent(proctoringSessionId, event) {
    try {
      this.emitter.emit("log", event);
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: event.type,
          name: event.name,
          time: event.time,
          details: event.details
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  disable = async (proctoringSessionId, details) => {
    await this.#logAccessCodeEvent(proctoringSessionId, {
      type: "access code",
      name: "save password disabled",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  reset = async (proctoringSessionId, details) => {
    await this.#logAccessCodeEvent(proctoringSessionId, {
      type: "access code",
      name: "save password reset",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  autofill = async (proctoringSessionId, details) => {
    await this.#logAccessCodeEvent(proctoringSessionId, {
      type: "access code",
      name: "autofill",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
};

// src/proctoring-session/logger/clipboard/index.ts
import { proctoringSessionLoggerClipboardSchemaValidators as schemaValidators3 } from "@yujacorp/verity-api-contract/schemas/typebox";
var ClipboardLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schemaValidators3);
    this.emitter = emitter2;
  }
  async #logClipboardEvent(proctoringSessionId, event) {
    try {
      this.emitter.emit("log", event);
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: event.type,
          name: event.name,
          time: event.time,
          details: event.details
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  disable = async (proctoringSessionId, details) => {
    await this.#logClipboardEvent(proctoringSessionId, {
      type: "clipboard",
      name: "disable",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  keypress = async (proctoringSessionId, details) => {
    await this.#logClipboardEvent(proctoringSessionId, {
      type: "clipboard",
      name: "keypress",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
};

// src/proctoring-session/logger/http/index.ts
import { proctoringSessionLoggerHttpSchemaValidators as schemaValidators4 } from "@yujacorp/verity-api-contract/schemas/typebox";
var HttpLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schemaValidators4);
    this.emitter = emitter2;
  }
  webNavigation = async (proctoringSessionId, name, details) => {
    try {
      const event = {
        type: "web navigation",
        name,
        time: /* @__PURE__ */ new Date(),
        details
      };
      this.emitter.emit("log", event);
      if (schemaValidators4.httpWebNavigationEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/proctoring-session/logger/screenshot/index.ts
import { proctoringSessionLoggerScreenshotSchemaValidators as schemaValidators5 } from "@yujacorp/verity-api-contract/schemas/typebox";
var ScreenshotLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schemaValidators5);
    this.emitter = emitter2;
  }
  async #logScreenshotEvent(proctoringSessionId, event) {
    try {
      this.emitter.emit("log", event);
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: event.type,
          name: event.name,
          time: event.time,
          details: event.details
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  disable = async (proctoringSessionId, details) => {
    await this.#logScreenshotEvent(proctoringSessionId, {
      type: "screenshot",
      name: "disable",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  keypress = async (proctoringSessionId, details) => {
    await this.#logScreenshotEvent(proctoringSessionId, {
      type: "screenshot",
      name: "keypress",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
};

// src/proctoring-session/logger/session/index.ts
import { proctoringSessionLoggerSessionSchemaValidators as schemaValidators6 } from "@yujacorp/verity-api-contract/schemas/typebox";
var SessionLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schemaValidators6);
    this.emitter = emitter2;
  }
  async #proctoringEvent({
    proctoringSessionId,
    event
  }) {
    try {
      this.emitter.emit("log", event);
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event),
        credentials: "include"
      });
      if (!response.ok) {
        throw new ApiClientError(new Error(`Invalid Response: ${response.status} - ${response.statusText}`));
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
  }
  startAssessment = async ({
    proctoringSessionId,
    event
  }) => {
    return await this.#proctoringEvent({
      proctoringSessionId,
      event: {
        type: "session",
        name: "start quiz",
        ...event
      }
    });
  };
  endAssessment = async ({
    proctoringSessionId,
    event
  }) => {
    return await this.#proctoringEvent({
      proctoringSessionId,
      event: {
        type: "session",
        name: "end quiz",
        ...event
      }
    });
  };
  endProctoring = async ({
    proctoringSessionId,
    event
  }) => {
    return await this.#proctoringEvent({
      proctoringSessionId,
      event: {
        type: "session",
        name: "end proctoring",
        ...event
      }
    });
  };
};

// src/proctoring-session/logger/tab/index.ts
import { proctoringSessionLoggerTabSchemaValidators as schemaValidators7 } from "@yujacorp/verity-api-contract/schemas/typebox";
var TabLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schemaValidators7);
    this.emitter = emitter2;
  }
  async #logTabEvent(proctoringSessionId, event) {
    try {
      this.emitter.emit("log", event);
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: event.type,
          name: event.name,
          time: event.time,
          details: event.details
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  closeOne = async (proctoringSessionId, details) => {
    await this.#logTabEvent(proctoringSessionId, {
      type: "tab",
      name: "close one",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  create = async (proctoringSessionId, details) => {
    await this.#logTabEvent(proctoringSessionId, {
      type: "tab",
      name: "create",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  whitelist = async (proctoringSessionId, details) => {
    await this.#logTabEvent(proctoringSessionId, {
      type: "tab",
      name: "whitelist",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  disable = async (proctoringSessionId, details) => {
    await this.#logTabEvent(proctoringSessionId, {
      type: "tab",
      name: "disable",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  focus = async (proctoringSessionId, details) => {
    await this.#logTabEvent(proctoringSessionId, {
      type: "tab",
      name: "focus",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
};

// src/proctoring-session/logger/keyboard/index.ts
import { proctoringSessionLoggerKeyboardSchemaValidators as schemaValidators8 } from "@yujacorp/verity-api-contract/schemas/typebox";
var KeyboardLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schemaValidators8);
    this.emitter = emitter2;
  }
  async #logKeyboardEvent(proctoringSessionId, event) {
    try {
      this.emitter.emit("log", event);
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: event.type,
          name: event.name,
          time: event.time,
          details: event.details
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  keypress = async (proctoringSessionId, details) => {
    await this.#logKeyboardEvent(proctoringSessionId, {
      type: "keyboard",
      name: "keypress",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
};

// src/proctoring-session/logger/index.ts
import EventEmitter2 from "events";

// src/proctoring-session/logger/live/index.ts
import { proctoringSessionLoggerLiveSchemaValidators as schemaValidators9 } from "@yujacorp/verity-api-contract/schemas/typebox";
var LiveLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schemaValidators9);
    this.emitter = emitter2;
  }
  createAudioBridge = async (proctoringSessionId, details) => {
    try {
      const event = {
        type: "live",
        name: "audio bridge",
        time: /* @__PURE__ */ new Date(),
        details
      };
      this.emitter.emit("log", event);
      if (schemaValidators9.liveBuildAudioBridgeEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  proctorJoined = async (proctoringSessionId, details) => {
    try {
      const event = {
        type: "live",
        name: "proctor joined",
        time: /* @__PURE__ */ new Date(),
        details
      };
      this.emitter.emit("log", event);
      if (schemaValidators9.liveProctorJoinedEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  proctorLeft = async (proctoringSessionId, details) => {
    try {
      const event = {
        type: "live",
        name: "proctor left",
        time: /* @__PURE__ */ new Date(),
        details
      };
      this.emitter.emit("log", event);
      if (schemaValidators9.liveProctorLeftEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  joinedSession = async (proctoringSessionId, details) => {
    try {
      const event = {
        type: "live",
        name: "join session",
        time: /* @__PURE__ */ new Date(),
        details
      };
      this.emitter.emit("log", event);
      if (schemaValidators9.liveJoinSessionEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  noProctors = async (proctoringSessionId, details) => {
    try {
      const event = {
        type: "live",
        name: "no proctors",
        time: /* @__PURE__ */ new Date(),
        details
      };
      this.emitter.emit("log", event);
      if (schemaValidators9.noProctorsEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/proctoring-session/logger/system/index.ts
import {
  proctoringSessionLoggerSystemSchemaValidators as schemaValidators10
} from "@yujacorp/verity-api-contract/schemas/typebox";
var SystemLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schemaValidators10);
    this.emitter = emitter2;
  }
  connectionCheck = async ({
    proctoringSessionId,
    details
  }) => {
    const event = {
      type: "system",
      name: "connection check",
      time: /* @__PURE__ */ new Date(),
      details
    };
    this.emitter.emit("log", event);
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event),
        credentials: "include"
      })
    );
  };
  systemResources = async ({
    proctoringSessionId,
    details
  }) => {
    const event = {
      type: "system",
      name: "system resources",
      time: /* @__PURE__ */ new Date(),
      details
    };
    this.emitter.emit("log", event);
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event),
        credentials: "include"
      })
    );
  };
  notInGeofence = async ({
    proctoringSessionId,
    details
  }) => {
    const event = {
      type: "system",
      name: "not in geofence",
      time: /* @__PURE__ */ new Date(),
      details
    };
    this.emitter.emit("log", event);
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event),
        credentials: "include"
      })
    );
  };
  locationUnknown = async ({
    proctoringSessionId,
    details
  }) => {
    const event = {
      type: "system",
      name: "location unknown",
      time: /* @__PURE__ */ new Date(),
      details
    };
    this.emitter.emit("log", event);
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event),
        credentials: "include"
      })
    );
  };
  installedExtensionsSnapshot = async ({
    proctoringSessionId,
    details
  }) => {
    const event = {
      type: "system",
      name: "installed extensions snapshot",
      time: /* @__PURE__ */ new Date(),
      details
    };
    this.emitter.emit("log", event);
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event),
        credentials: "include"
      })
    );
  };
};

// src/proctoring-session/logger/face-detection/index.ts
import { proctoringSessionLoggerFaceDetectionSchemaValidators as schemaValidators11 } from "@yujacorp/verity-api-contract/schemas/typebox";
var FaceDetectionLoggerClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators11);
  }
  async facesDetected(proctoringSessionId, { who, count }) {
    try {
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "face detection",
          name: who,
          time: /* @__PURE__ */ new Date(),
          details: {
            count
          }
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
};

// src/proctoring-session/logger/interrupt/index.ts
import { proctoringSessionLoggerInterruptSchemaValidators as schemaValidators12 } from "@yujacorp/verity-api-contract/schemas/typebox";
var InterruptLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schemaValidators12);
    this.emitter = emitter2;
  }
  async #logInterruptEvent(proctoringSessionId, event) {
    try {
      this.emitter.emit("log", event);
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: event.type,
          name: event.name,
          time: event.time,
          details: event.details
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  trigger = async (proctoringSessionId, details) => {
    await this.#logInterruptEvent(proctoringSessionId, {
      type: "interrupt",
      name: "trigger",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  resume = async (proctoringSessionId, details) => {
    await this.#logInterruptEvent(proctoringSessionId, {
      type: "interrupt",
      name: "resume",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
};

// src/proctoring-session/logger/user-action/index.ts
import { proctoringSessionLoggerUserActionSchemaValidators as schemaValidators13 } from "@yujacorp/verity-api-contract/schemas/typebox";
var UserActionLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schemaValidators13);
    this.emitter = emitter2;
  }
  async #logUserActionEvent(proctoringSessionId, event) {
    try {
      this.emitter.emit("log", event);
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: event.type,
          name: event.name,
          time: event.time,
          details: event.details
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  sidePanelDetected = async (proctoringSessionId, details) => {
    await this.#logUserActionEvent(proctoringSessionId, {
      type: "user action",
      name: "side panel detected",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  rightClickBlocked = async (proctoringSessionId, details) => {
    await this.#logUserActionEvent(proctoringSessionId, {
      type: "user action",
      name: "right click blocked",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  printingBlocked = async (proctoringSessionId, details) => {
    await this.#logUserActionEvent(proctoringSessionId, {
      type: "user action",
      name: "printing blocked",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  logTypedSignatureEvent = async ({
    proctoringSessionId,
    details
  }) => {
    try {
      const event = {
        type: "user action",
        name: "typed signature",
        time: /* @__PURE__ */ new Date(),
        details: { signature: details.signature }
      };
      if (schemaValidators13.typedSignatureEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/proctoring-session/logger/worker/index.ts
import { proctoringSessionLoggerWorkerSchemaValidators as schemaValidators14 } from "@yujacorp/verity-api-contract/schemas/typebox";
var WorkerLoggerClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators14);
  }
  logFaceWorkerEvent = async ({
    sessionId,
    event
  }) => {
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event)
      })
    );
  };
  logObjectWorkerEvent = async ({
    sessionId,
    event
  }) => {
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event)
      })
    );
  };
};

// src/proctoring-session/logger/media/index.ts
import { proctoringSessionLoggerMediaSchemaValidators as schemaValidators15 } from "@yujacorp/verity-api-contract/schemas/typebox";
var MediaLoggerClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators15);
  }
  logStartRecording = async ({
    sessionId,
    recordingDetails
  }) => {
    const response = await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "session",
          name: "start recording",
          time: /* @__PURE__ */ new Date(),
          details: { ...recordingDetails, success: true }
        })
      })
    );
    try {
      const data = await response.json();
      if (!schemaValidators15.logStartRecording.validate(data)) {
        throw new ApiClientError(new Error(`Invalid log start recording: ${JSON.stringify(data)}`));
      }
      return data;
    } catch (error) {
      return "";
    }
  };
  logEndRecording = async ({
    sessionId,
    recordingDetails
  }) => {
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "session",
          name: "end recording",
          time: /* @__PURE__ */ new Date(),
          details: { ...recordingDetails, success: true }
        })
      })
    );
  };
  logEndUpload = async ({
    sessionId,
    recordingDetails
  }) => {
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "session",
          name: "end upload",
          time: /* @__PURE__ */ new Date(),
          details: recordingDetails
        })
      })
    );
  };
};

// src/proctoring-session/logger/machine-learning/index.ts
import { LogMachineLearningQuizEventContract, coerceToContractAbsoluteBase, withContractBase } from "@yujacorp/verity-api-contract";
var MachineLearningLoggerClient = class extends ApiClient {
  constructor(config) {
    super(config, {});
  }
  logMachineLearningQuizEvent = withDefaultErrorHandler(async (dynamicInput) => {
    const handlers = withContractBase({
      contract: LogMachineLearningQuizEventContract,
      base: coerceToContractAbsoluteBase(`${this.config.verityOrigin}/api/verity/`)
    }).withFetchParams({
      pathParams: dynamicInput.pathParams,
      body: dynamicInput.body
    }).withFetcher(async ({ method, url, body }) => {
      const res = await this.fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : void 0
      });
      return { ok: res.ok };
    });
    return handlers.toRaw().then((value) => value.ok);
  });
};

// src/proctoring-session/logger/id/index.ts
import { proctoringSessionLoggerIdSchemaValidators as schemaValidators16 } from "@yujacorp/verity-api-contract/schemas/typebox";
var IdLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schemaValidators16);
    this.emitter = emitter2;
  }
  logIdEvent = async ({
    proctoringSessionId,
    name,
    details
  }) => {
    try {
      const event = {
        type: "id",
        name,
        time: /* @__PURE__ */ new Date(),
        details: name === "face id mismatch" ? details : { success: true }
      };
      this.emitter.emit("log", event);
      if (schemaValidators16.idEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/proctoring-session/logger/index.ts
var emitter = new EventEmitter2();
var LoggerClient = class extends ApiClient {
  TabLoggerClient;
  HttpLoggerClient;
  AccessCodeLoggerClient;
  ClipboardLoggerClient;
  ScreenshotLoggerClient;
  SessionLoggerClient;
  InterruptLoggerClient;
  KeyboardLoggerClient;
  LiveLoggerClient;
  SystemLoggerClient;
  FaceDetectionLoggerClient;
  UserActionLoggerClient;
  WorkerLoggerClient;
  MediaLoggerClient;
  MachineLearningLoggerClient;
  IdLoggerClient;
  on;
  off;
  once;
  // readonly ToolbarLoggerClient: ToolbarLoggerClient;
  constructor(config) {
    super(config, {});
    this.TabLoggerClient = new TabLoggerClient(config, emitter);
    this.HttpLoggerClient = new HttpLoggerClient(config, emitter);
    this.AccessCodeLoggerClient = new AccessCodeLoggerClient(config, emitter);
    this.ClipboardLoggerClient = new ClipboardLoggerClient(config, emitter);
    this.ScreenshotLoggerClient = new ScreenshotLoggerClient(config, emitter);
    this.SessionLoggerClient = new SessionLoggerClient(config, emitter);
    this.InterruptLoggerClient = new InterruptLoggerClient(config, emitter);
    this.KeyboardLoggerClient = new KeyboardLoggerClient(config, emitter);
    this.LiveLoggerClient = new LiveLoggerClient(config, emitter);
    this.SystemLoggerClient = new SystemLoggerClient(config, emitter);
    this.FaceDetectionLoggerClient = new FaceDetectionLoggerClient(config);
    this.UserActionLoggerClient = new UserActionLoggerClient(config, emitter);
    this.WorkerLoggerClient = new WorkerLoggerClient(config);
    this.MediaLoggerClient = new MediaLoggerClient(config);
    this.MachineLearningLoggerClient = new MachineLearningLoggerClient(config);
    this.IdLoggerClient = new IdLoggerClient(config, emitter);
    this.on = emitter.on.bind(emitter);
    this.once = emitter.once.bind(emitter);
    this.off = emitter.off.bind(emitter);
  }
};

// src/proctoring-session/state/index.ts
import { proctoringSessionStateSchemaValidators as schemaValidators17 } from "@yujacorp/verity-api-contract/schemas/typebox";
var stateClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators17);
  }
  get = async (uuid) => {
    try {
      const response = await super.fetch(new URL(`${this.config.verityOrigin}/api/verity/quiz/state/${uuid}`), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.proctoringSessionState.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  set = async (uuid, status) => {
    try {
      const response = await super.fetch(new URL(`${this.config.verityOrigin}/api/verity/quiz/state/${uuid}`), {
        headers: {
          "Content-Type": "application/json"
        },
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          status
        })
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.proctoringSessionState.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/proctoring-session/index.ts
import {
  withContractBase as withContractBase2,
  coerceToContractAbsoluteBase as coerceToContractAbsoluteBase2,
  GetProctoringSessionFlaggedKeywordsContract
} from "@yujacorp/verity-api-contract";
var ProctoringSessionClient = class extends ApiClient {
  logger;
  stateClient;
  constructor(config) {
    super(config, schemaValidators18);
    this.logger = new LoggerClient(config);
    this.stateClient = new stateClient(config);
  }
  startProctoring = async (assessmentInfo, devicesInfo, capabilities) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/quiz/event/${assessmentInfo.lms ?? "verity"}/${assessmentInfo.hostname ?? location.hostname}/${assessmentInfo.courseId ?? ""}/${assessmentInfo.quizId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "session",
          name: "start proctoring",
          time: /* @__PURE__ */ new Date(),
          details: { success: true, devicesInfo, capabilities }
        }),
        credentials: "include"
      }
    ),
    this.schemaValidators.startProctoring
  );
  upload = async (recordingBlobInfo, blob) => {
    const searchParams = new URLSearchParams({
      segment: `${recordingBlobInfo.blobNumber}`,
      url: "upload"
    });
    const response = await super.fetch(
      `${this.config.verityOrigin}/api/verity/quiz/recording/${recordingBlobInfo.proctoringSessionId}/${recordingBlobInfo.recordingType === "display" ? "screencapture" : recordingBlobInfo.recordingType}/${recordingBlobInfo.recordingNumber}?${searchParams.toString()}`,
      {
        credentials: "include"
      }
    );
    if (!response.ok) {
      throw new ApiClientError(
        new Error(`Upload to get S3 pre-signed URL: ${response.status} - ${response.statusText}`)
      );
    }
    const uploadUrl = await response.json();
    if (schemaValidators18.uploadS3Url.validate(uploadUrl)) {
      const s3Response = await super.fetch(uploadUrl.url, {
        method: "PUT",
        body: blob
      });
      if (!s3Response.ok) {
        throw new ApiClientError(new Error(`Upload to S3 failed: ${s3Response.status} - ${s3Response.statusText}`));
      }
    }
  };
  uploadRoomScan = async (proctoringSessionId, blob) => {
    const response = await super.fetch(
      `${this.config.verityOrigin}/api/verity/quiz/recording/${proctoringSessionId}/room_scan.webm`,
      {
        method: "PUT",
        body: blob
      }
    );
    if (!response.ok) {
      throw new ApiClientError(new Error(`Upload to S3 failed: ${response.status} - ${response.statusText}`));
    }
  };
  getRoomScanUrl = async (proctoringSessionId) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/recording/${proctoringSessionId}/room_scan/latest.webm`,
        {
          method: "GET"
        }
      );
      if (!response.ok) {
        throw new ApiClientError(
          new Error(`Failed to retrieve room scan video: ${response.status} - ${response.statusText}`)
        );
      }
      const videoUrl = response.url;
      return videoUrl;
    } catch (error) {
      console.error("Error fetching room scan video: ", error);
      throw error;
    }
  };
  getCachedRoomScanUrl = async (proctoringSessionId) => {
    try {
      const urlResponse = await super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/recording/${proctoringSessionId}/room_scan/url`
      );
      if (urlResponse.ok) {
        const resObj = await urlResponse.json();
        if (this.schemaValidators.url.validate(resObj)) {
          const videoResponse = await super.fetch(resObj.url, { method: "HEAD" });
          if (videoResponse.ok) {
            return resObj.url;
          }
        }
      }
    } catch (error) {
      console.error("Error fetching room scan video: ", error);
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    return void 0;
  };
  getPhotoUrl = async (proctoringSessionId, type) => {
    const ext = "png";
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/photo/${proctoringSessionId}-${type}.${ext}`
      );
      if (response.ok) {
        const imageBlob = await response.blob();
        return URL.createObjectURL(imageBlob);
      }
    } catch (error) {
      console.error(`Error fetching ${type} photo: `, error);
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    return void 0;
  };
  getPhotoUrlJson = async (proctoringSessionId, type) => {
    const ext = "png";
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/photo/${proctoringSessionId}-${type}.${ext}?format=json`
      );
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error(`Error fetching ${type} photo: `, error);
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    return void 0;
  };
  getLmsContext = async (sessionId) => {
    try {
      const res = await super.fetch(
        `${this.config.verityOrigin}/api/verity/proctoring-session/${sessionId}/lms-context`
      );
      if (res.ok) {
        const lmsContext = await res.json();
        if (this.schemaValidators.lmsContext.validate(lmsContext)) {
          return lmsContext;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  async isCorrectProctoringSessionId(sessionId, assessmentInfo) {
    try {
      const lmsContext = await this.getLmsContext(sessionId);
      return assessmentInfo.quizId === lmsContext.assessment.quizId;
    } catch (err) {
      console.error(err);
    }
    return false;
  }
  async getSessionResultList(lmsQuizId, courseId) {
    try {
      const res = await super.fetch(
        `${this.config.verityOrigin}/api/verity/test-results/${courseId}/${lmsQuizId}?session=all`,
        {
          credentials: "include"
        }
      );
      if (res.ok) {
        const events = await res.json();
        if (this.schemaValidators.testResults.validate(events)) {
          return events;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async getSessionList(courseId) {
    try {
      const res = await super.fetch(`${this.config.verityOrigin}/api/verity/test-results/${courseId}?session=all`, {
        credentials: "include"
      });
      if (res.ok) {
        const events = await res.json();
        if (this.schemaValidators.testResults.validate(events)) {
          return events;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async getSessionLoggedEvents({ sessionId, courseId }) {
    try {
      const res = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${courseId}/${sessionId}`, {
        credentials: "include"
      });
      if (res.ok) {
        const events = await res.json();
        if (this.schemaValidators.proctoringEvent.validate(events)) {
          return events;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async getVideoUrls({ sessionId, recordingType }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/recording/url/${sessionId}/${recordingType}`,
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async requestVideoConcatenation(sessionId) {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/recording/${sessionId}/concatenated-recording`, {
        method: "POST",
        credentials: "include"
      }),
      this.schemaValidators.concatenationRequestId
    );
  }
  async getConcatenationRequest(sessionId, requestId) {
    return handleApiErrorWithSchema(
      super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/recording/${sessionId}/concatenation-request/${requestId}`,
        {
          credentials: "include"
        }
      ),
      this.schemaValidators.concatenationRequest
    );
  }
  async deleteRecordingsFromSession(sessionUuid) {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/recording/${sessionUuid}`, {
        method: "DELETE"
      });
      if (!response.ok) {
        throw new ApiClientError();
      }
      return true;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async deleteRoomScanFromSession(sessionUuid) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/recording/${sessionUuid}/room_scan.webm`,
        {
          method: "DELETE"
        }
      );
      if (!response.ok) {
        throw new ApiClientError();
      }
      return true;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async getSessionNotes({ sessionId, courseId }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/session-notes/${courseId}/${sessionId}`,
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.sessionNotes.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async addSessionNote({
    sessionId,
    courseId,
    content,
    timestamp
  }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/session-notes/${courseId}/${sessionId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            content,
            timestamp
          })
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async editSessionNote({
    sessionId,
    courseId,
    noteId,
    content
  }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/session-notes/${courseId}/${sessionId}/${noteId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            content
          })
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async deleteSessionNote({ sessionId, courseId, noteId }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/session-notes/${courseId}/${sessionId}/${noteId}`,
        {
          method: "DELETE",
          credentials: "include"
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async getRoomScanNotes({ sessionId, courseId }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/roomscan-notes/${courseId}/${sessionId}`,
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.roomScanNotes.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async addRoomScanNote({
    sessionId,
    courseId,
    content,
    timestamp
  }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/roomscan-notes/${courseId}/${sessionId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            content,
            timestamp
          })
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async editRoomScanNote({
    sessionId,
    courseId,
    noteId,
    content
  }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/roomscan-notes/${courseId}/${sessionId}/${noteId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            content
          })
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async deleteRoomScanNote({ sessionId, courseId, noteId }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/roomscan-notes/${courseId}/${sessionId}/${noteId}`,
        {
          method: "DELETE",
          credentials: "include"
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async getOwnResultsCount(courseId, lmsProvidedAssessmentId) {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/test-results/${courseId}/${lmsProvidedAssessmentId}/count`, {
        credentials: "include"
      }),
      this.schemaValidators.count
    );
  }
  async get(proctoringSessionId) {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/proctoring-session/${proctoringSessionId}`, {
        credentials: "include"
      }),
      this.schemaValidators.proctoringSession
    );
  }
  async createCustomEvent({
    proctoringSessionId,
    message,
    flagLevel,
    time
  }) {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          type: "custom",
          name: "live",
          details: {
            message,
            flagLevel
          },
          time
        })
      }),
      this.schemaValidators.singleEvent
    );
  }
  async updateCustomEvent({
    proctoringSessionId,
    eventId,
    message
  }) {
    return handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}/${eventId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          details: {
            message
          }
        })
      })
    );
  }
  async createCustomEventFlag({
    proctoringSessionId,
    eventId,
    flagLevel
  }) {
    return handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}/${eventId}/anomaly`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ flagLevel })
      })
    );
  }
  async updateEventFlag({
    proctoringSessionId,
    eventId,
    resolved,
    flagLevel,
    comment
  }) {
    return handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}/${eventId}/anomaly`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          resolved,
          flagLevel,
          comment
        })
      })
    );
  }
  getFlaggedKeywords = withDefaultErrorHandler(
    async (dynamicInput) => {
      const handlers = withContractBase2({
        contract: GetProctoringSessionFlaggedKeywordsContract,
        base: coerceToContractAbsoluteBase2(`${this.config.verityOrigin}/api/verity/`)
      }).withFetchParams({
        pathParams: dynamicInput.pathParams
      }).withFetcher(async ({ method, url }) => {
        const res = await this.fetch(url, {
          method,
          headers: { "Content-Type": "application/json" }
        });
        return res.json();
      });
      return handlers.toParsed();
    }
  );
  getProctoringEvents({
    assessmentIds = [],
    types = []
  }) {
    const searchParams = new URLSearchParams();
    assessmentIds.forEach((id) => searchParams.append("assessmentId", String(id)));
    if (types.length > 0) {
      searchParams.set("type", JSON.stringify(types));
    }
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event?${searchParams.toString()}`),
      this.schemaValidators.paginatedEventsWithSessions
    );
  }
  getSummary = async (proctoringSessionId) => {
    return handleApiErrorWithSchema(
      super.fetch(
        `${this.config.verityOrigin}/api/verity/test-results/proctoring/session/${proctoringSessionId}/summary`,
        {
          method: "GET",
          credentials: "include"
        }
      ),
      this.schemaValidators.sessionSummary
    );
  };
  generateAiSummary = async (proctoringSessionId, events) => {
    return handleApiErrorWithSchema(
      super.fetch(
        `${this.config.verityOrigin}/api/verity/test-results/proctoring/session/${proctoringSessionId}/ai-summary`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(events)
        }
      ),
      this.schemaValidators.sessionSummary
    );
  };
  generateDefaultLiveProctorSummary = async (proctoringSessionId, events) => (await handleApiError(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/test-results/proctoring/session/${proctoringSessionId}/default-live-proctor-summary`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(events)
      }
    )
  )).text();
  updateLiveProctorSummary = (proctoringSessionId, summary) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/test-results/proctoring/session/${proctoringSessionId}/live-proctor-summary`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ summary })
      }
    ),
    this.schemaValidators.sessionSummary
  );
  requestSupportChatResponse = async ({ sessionId, messages }) => {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/setup/${sessionId}/ai-support`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chatHistory: messages })
      }),
      this.schemaValidators.supportChatResponse
    );
  };
  requestSupportChatHistory = async ({ sessionId }) => {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/setup/${sessionId}/ai-support/history`, {
        method: "GET"
      }),
      this.schemaValidators.supportChatHistoryResponse
    );
  };
  getAssessmentSubmissionUploadUrl = ({
    proctoringSessionUuid,
    mimeType,
    fileName,
    fileSize
  }) => {
    const baseUrl = `${this.config.verityOrigin}/api/verity/proctoring-session/${proctoringSessionUuid}/assessment-submission-url`;
    const params = new URLSearchParams({
      mimeType,
      fileName,
      fileSize: fileSize.toString()
    });
    const url = `${baseUrl}?${params.toString()}`;
    return handleApiErrorWithSchema(
      super.fetch(url, {
        credentials: "include"
      }),
      this.schemaValidators.assessmentSubmissionUploadUrlResponse
    );
  };
  getPublicAssessmentSubmissionUploadUrl = ({
    proctoringSessionUuid,
    mimeType,
    fileName,
    fileSize
  }) => {
    const baseUrl = `${this.config.verityOrigin}/api/verity/photo/${proctoringSessionUuid}/assessment-submission-url`;
    const params = new URLSearchParams({
      mimeType,
      fileName,
      fileSize: fileSize.toString()
    });
    const url = `${baseUrl}?${params.toString()}`;
    return handleApiErrorWithSchema(
      super.fetch(url, {
        credentials: "include"
      }),
      this.schemaValidators.assessmentSubmissionUploadUrlResponse
    );
  };
  getAssessmentFileSubmissionStudent = (proctoringSessionId) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/proctoring-session/${proctoringSessionId}/assessment-submission`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.getAssessmentSubmissionResponse
  );
  getAssessmentFileSubmissionCountStudent = (proctoringSessionId) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/proctoring-session/${proctoringSessionId}/assessment-submission-count`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.getAssessmentSubmissionCount
  );
  getAssessmentFileSubmissions = (proctoringSessionId) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/proctoring-session/${proctoringSessionId}/instructor/assessment-submissions`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.getAssessmentSubmissionResponse
  );
  getStudentSubmissionsByAssessment = (quizId) => {
    const url = `${this.config.verityOrigin}/api/verity/proctoring-session/${quizId}/assessment-submissions-list`;
    return handleApiErrorWithSchema(
      super.fetch(url, {
        credentials: "include"
      }),
      this.schemaValidators.getStudentSubmissionsByAssessmentResponse
    );
  };
  getStudentQuizSessionData = (proctoringSessionId) => handleApiErrorWithSchema(
    super.fetch(`${this.config.verityOrigin}/api/verity/proctoring-session/${proctoringSessionId}/quiz`, {
      credentials: "include"
    }),
    this.schemaValidators.studentQuizSessionData
  );
  updateQuizResponses = (proctoringSessionId, body) => handleApiErrorWithSchema(
    super.fetch(`${this.config.verityOrigin}/api/verity/proctoring-session/${proctoringSessionId}/quiz`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body)
    }),
    this.schemaValidators.studentQuizSessionData
  );
  getInstructorQuizSessionData = (proctoringSessionId) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/test-results/proctoring/session/${proctoringSessionId}/quiz`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.instructorQuizSessionData
  );
  updateQuizResponsesInstructor = (proctoringSessionId, body) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/test-results/proctoring/session/${proctoringSessionId}/quiz-responses`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
      }
    ),
    this.schemaValidators.instructorQuizSessionData
  );
};

// src/live/index.ts
import {
  liveSchemaValidators as schemaValidators19
} from "@yujacorp/verity-api-contract/schemas/typebox";
var emptyPaginatedResults = {
  currentPage: 0,
  totalElements: 0,
  totalPages: 1,
  results: []
};
var LiveClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators19);
  }
  getLiveSessionsWithUsers = ({
    instituteUuid,
    courseUuid,
    assessmentUuid,
    sortBy,
    order,
    size,
    page = 0
  }) => {
    const searchParams = new URLSearchParams();
    if (instituteUuid) {
      searchParams.set("instituteId", instituteUuid);
    }
    if (courseUuid) {
      searchParams.set("courseId", courseUuid);
    }
    if (assessmentUuid) {
      searchParams.set("assessmentId", assessmentUuid);
    }
    if (sortBy) {
      searchParams.set("sortBy", sortBy);
    }
    if (order) {
      searchParams.set("order", order);
    }
    if (size != null) {
      searchParams.set("size", String(size));
    }
    if (page != null) {
      searchParams.set("page", String(page));
    }
    searchParams.set("includeUsers", "true");
    return handleApiErrorWithSchema(
      super.fetch(new URL(`/api/live/liveSession?${searchParams}`, `${this.config.verityOrigin}`)),
      this.schemaValidators.liveSessionListWithUsers
    );
  };
  getCourseLiveSessionInfo = async (instituteUuid, courseUuid) => {
    try {
      const resp = await super.fetch(
        new URL(
          `/api/live/liveSession?instituteId=${instituteUuid}&courseId=${courseUuid}`,
          `${this.config.verityOrigin}`
        )
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.liveSessionList.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    console.error("Failed to retrieve course live session info");
    return emptyPaginatedResults;
  };
  getLiveSession = async (liveSessionId) => {
    try {
      const resp = await super.fetch(new URL(`/api/live/liveSession/${liveSessionId}`, `${this.config.verityOrigin}`));
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.singleLiveSession.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getLiveSessionLiveState = async (liveSessionId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/live/liveSession/${liveSessionId}/liveState`, `${this.config.verityOrigin}`)
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.liveSessionLiveState.validate(data)) {
          return data;
        }
      } else {
        return void 0;
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getLiveSessionUser = async (liveSessionId, userId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/live/liveSession/${liveSessionId}/user/${userId}`, `${this.config.verityOrigin}`)
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.liveSessionUser.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getLiveSessionInvitations = async (liveSessionId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/live/liveSession/${liveSessionId}/invitation`, `${this.config.verityOrigin}`),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.liveSessionInvitations.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getCourseLiveProctorInfo = async (lmsProvidedCourseId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/user/instructor-roster/${lmsProvidedCourseId}`, `${this.config.verityOrigin}`),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.courseLiveProctorInfo.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    console.error("Failed to retrieve course live proctor info");
    return [];
  };
  getLiveProctorGroup = async (proctorGroupId) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/live/proctorGroup/${proctorGroupId}`, `${this.config.verityOrigin}`), {
      credentials: "include"
    }),
    this.schemaValidators.liveProctorGroup
  );
  getUserProctorGroups = async (userId, userTypes = ["proctor"]) => {
    const searchParams = new URLSearchParams();
    userTypes.forEach((type) => searchParams.append("userType", type));
    return handleApiErrorWithSchema(
      super.fetch(
        new URL(`/api/live/user/${userId}/proctorGroup?${searchParams.toString()}`, `${this.config.verityOrigin}`),
        {
          credentials: "include"
        }
      ),
      this.schemaValidators.liveProctorGroups
    );
  };
  getLiveInstituteProctorGroup = async (instituteUuid) => {
    let proctorGroupResults = [];
    try {
      proctorGroupResults = (await handleApiErrorWithSchema(
        super.fetch(new URL(`/api/live/proctorGroup?instituteId=${instituteUuid}`, `${this.config.verityOrigin}`), {
          credentials: "include"
        }),
        this.schemaValidators.instituteProctorGroups
      )).results;
    } catch (e) {
      console.error("Failed to retrieve institute proctor groups", e);
      try {
        const userId = (await this.getJwt())?.uid;
        if (userId) {
          proctorGroupResults = await this.getUserProctorGroups(userId);
        }
      } catch (e2) {
        console.error("Failed to retrieve user proctor groups", e2);
      }
    }
    return proctorGroupResults.filter(({ allowedInstitutes }) => allowedInstitutes.some(({ instituteId }) => instituteId === instituteUuid)).map((proctorGroup) => {
      const { minHoursNotice, trackCancellationHoursBefore, trackCancellationMessage, autoCancelEmptyHoursBefore } = proctorGroup.allowedInstitutes.find(({ instituteId }) => instituteId === instituteUuid);
      return {
        ...proctorGroup,
        minHoursNotice,
        trackCancellationHoursBefore,
        trackCancellationMessage,
        autoCancelEmptyHoursBefore
      };
    });
  };
  getAssessmentLiveSessionInfo = async ({ instituteUuid, courseUuid, assessmentUuid }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/live/liveSession?instituteId=${instituteUuid}&courseId=${courseUuid}&assessmentId=${assessmentUuid}&isPopIn=any`
      );
      const liveSessionInfo = await response.json();
      if (this.schemaValidators.liveSessionList.validate(liveSessionInfo)) {
        return liveSessionInfo;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getLiveSessionNotes = async (liveSessionId) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/live/liveSession/${liveSessionId}/notes`);
      const liveSessionNotes = await response.json();
      if (this.schemaValidators.liveSessionNotes.validate(liveSessionNotes)) {
        return liveSessionNotes;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getUserCourseLiveSessions = async (userUuid, courseUuid) => {
    try {
      const liveSessions = await (await super.fetch(`${this.config.verityOrigin}/api/live/user/${userUuid}/liveSession?courseId=${courseUuid}`)).json();
      if (this.schemaValidators.liveSessionList.validate(liveSessions)) {
        return liveSessions.results;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getUserAssessmentLiveSessions = async (assessmentUuid) => {
    try {
      const liveSessions = await (await super.fetch(
        `${this.config.verityOrigin}/api/live/user/${(await this.getJwt())?.uid}/liveSession?assessmentId=${assessmentUuid}&sortBy=endTime`
      )).json();
      if (this.schemaValidators.liveSessionList.validate(liveSessions)) {
        return liveSessions.results;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  doesLiveSessionExistForAssessment = async (assessmentUuidInfo) => {
    const result = await this.getAssessmentLiveSessionInfo(assessmentUuidInfo);
    return result.totalElements > 0;
  };
  getInstituteLiveSessions = async (instituteUuid) => {
    try {
      const liveSessions = await (await super.fetch(`${this.config.verityOrigin}/api/live/liveSession?instituteId=${instituteUuid}`)).json();
      if (this.schemaValidators.liveSessionList.validate(liveSessions)) {
        return liveSessions;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCourseLiveSessions = async (instituteUuid, courseUuid) => {
    try {
      const liveSessions = await (await super.fetch(
        `${this.config.verityOrigin}/api/live/liveSession?instituteId=${instituteUuid}&courseId=${courseUuid}`
      )).json();
      if (this.schemaValidators.liveSessionList.validate(liveSessions)) {
        return liveSessions;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getStudentInstituteId = async () => {
    try {
      const instituteId = await (await super.fetch(`${this.config.verityOrigin}/api/verity/user/institute-id`)).json();
      if (typeof instituteId === "number") {
        return instituteId;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  addLiveSession = async ({ instituteUuid, courseUuid, assessmentUuid }, payload) => {
    try {
      const resp = await super.fetch(new URL(`/api/live/liveSession`, `${this.config.verityOrigin}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...payload,
          users: payload.users ?? [],
          instituteId: instituteUuid,
          courseId: courseUuid,
          assessmentId: assessmentUuid
        }),
        credentials: "include"
      });
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.singleLiveSession.validate(data)) {
          return {
            success: resp.ok,
            status: resp.status,
            error: void 0,
            updatedSession: data
          };
        }
      } else {
        const errorMessage = await resp.text();
        return {
          success: resp.ok,
          status: resp.status,
          error: errorMessage.includes("html") ? void 0 : JSON.parse(errorMessage),
          updatedSession: void 0
        };
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return {
      success: false,
      error: void 0,
      updatedSession: void 0
    };
  };
  deleteLiveSession = async ({ liveSessionId }) => {
    try {
      const resp = await super.fetch(new URL(`/api/live/liveSession/${liveSessionId}`, `${this.config.verityOrigin}`), {
        method: "DELETE",
        credentials: "include"
      });
      if (resp.ok) {
        return {
          success: resp.ok,
          status: resp.status,
          error: void 0
        };
      } else {
        const errorMessage = await resp.text();
        return {
          success: resp.ok,
          status: resp.status,
          error: errorMessage.includes("html") ? void 0 : JSON.parse(errorMessage),
          updatedSession: void 0
        };
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return {
      success: false,
      error: void 0
    };
  };
  modifyLiveSession = async ({
    liveSessionId,
    payload
  }) => {
    try {
      const resp = await super.fetch(new URL(`/api/live/liveSession/${liveSessionId}`, `${this.config.verityOrigin}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include"
      });
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.singleLiveSession.validate(data)) {
          return {
            success: resp.ok,
            status: resp.status,
            error: void 0,
            updatedSession: data
          };
        }
      } else {
        const errorMessage = await resp.text();
        return {
          success: resp.ok,
          status: resp.status,
          error: errorMessage.includes("<!DOCTYPE html>") ? void 0 : JSON.parse(errorMessage),
          updatedSession: void 0
        };
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return {
      success: false,
      error: void 0,
      updatedSession: void 0
    };
  };
  saveLiveSessionInstituteUser = async ({
    liveSessionId,
    action,
    role,
    userId,
    silent = false
  }) => {
    try {
      let resp;
      if (action === "register") {
        resp = await super.fetch(
          new URL(`/api/live/liveSession/${liveSessionId}/user`, `${this.config.verityOrigin}`),
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId, role, silent }),
            credentials: "include"
          }
        );
      } else {
        resp = await super.fetch(
          new URL(`/api/live/liveSession/${liveSessionId}/user/${userId}`, `${this.config.verityOrigin}`),
          {
            method: "DELETE",
            credentials: "include"
          }
        );
      }
      if (resp.ok) {
        if (action === "register") {
          const data = await resp.json();
          if (this.schemaValidators.registerLiveSessionInstituteUser.validate(data)) {
            return {
              success: resp.ok,
              error: void 0,
              updatedSession: data
            };
          }
        } else if (action === "deregister") {
          return {
            success: resp.ok,
            error: void 0,
            updatedSession: void 0
          };
        }
      } else {
        const errorMessage = await resp.text();
        return {
          success: resp.ok,
          error: errorMessage.includes("<!DOCTYPE html>") ? void 0 : JSON.parse(errorMessage),
          updatedSession: void 0
        };
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return {
      success: false,
      error: void 0,
      updatedSession: void 0
    };
  };
  addLiveSessionUser = ({
    liveSessionId,
    role,
    userId
  }) => {
    return handleApiErrorWithSchema(
      super.fetch(new URL(`/api/live/liveSession/${liveSessionId}/user`, `${this.config.verityOrigin}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, role }),
        credentials: "include"
      }),
      this.schemaValidators.registerLiveSessionInstituteUser
    );
  };
  removeLiveSessionUser = async (liveSessionId, userId) => {
    await handleApiError(
      super.fetch(new URL(`/api/live/liveSession/${liveSessionId}/user/${userId}`, `${this.config.verityOrigin}`), {
        method: "DELETE",
        credentials: "include"
      })
    );
  };
  registerSelfAsProctor = async (liveSessionId) => {
    const userId = (await this.getJwt())?.uid;
    if (!userId) {
      throw new UnauthenticatedError();
    }
    try {
      await this.addLiveSessionUser({
        liveSessionId,
        userId,
        role: "proctor"
      });
    } catch (e) {
      if (!(e instanceof ApiClientResponseError) || e.code !== "userExists") {
        throw e;
      }
    }
  };
  saveLiveSessionProctorGroup = async ({
    liveSessionId,
    action,
    proctorGroupId
  }) => {
    try {
      let resp;
      if (action === "registerGroup") {
        resp = await super.fetch(
          new URL(`/api/live/liveSession/${liveSessionId}/invitation/${proctorGroupId}`, `${this.config.verityOrigin}`),
          {
            method: "POST",
            credentials: "include"
          }
        );
      } else {
        resp = await super.fetch(
          new URL(`/api/live/liveSession/${liveSessionId}/invitation/${proctorGroupId}`, `${this.config.verityOrigin}`),
          {
            method: "DELETE",
            credentials: "include"
          }
        );
      }
      if (resp.ok) {
        if (action === "registerGroup") {
          const data = await resp.json();
          if (this.schemaValidators.registerLiveSessionProctorGroup.validate(data)) {
            return {
              success: resp.ok,
              error: void 0,
              updatedSession: data
            };
          }
        } else if (action === "deregisterGroup") {
          return {
            success: resp.ok,
            error: void 0
          };
        }
      } else {
        const errorMessage = await resp.text();
        return {
          success: resp.ok,
          error: errorMessage.includes("<!DOCTYPE html>") ? void 0 : JSON.parse(errorMessage),
          updatedSession: void 0
        };
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return {
      success: false,
      error: void 0,
      updatedSession: void 0
    };
  };
  createProctorGroupInvitation = (liveSessionId, proctorGroupId) => {
    return handleApiErrorWithSchema(
      super.fetch(
        new URL(`/api/live/liveSession/${liveSessionId}/invitation/${proctorGroupId}`, `${this.config.verityOrigin}`),
        {
          method: "POST",
          credentials: "include"
        }
      ),
      this.schemaValidators.liveSessionInvitation
    );
  };
  deleteProctorGroupInvitation = async (liveSessionId, proctorGroupId) => {
    await handleApiError(
      super.fetch(
        new URL(`/api/live/liveSession/${liveSessionId}/invitation/${proctorGroupId}`, `${this.config.verityOrigin}`),
        {
          method: "DELETE",
          credentials: "include"
        }
      )
    );
  };
  markLiveSessionViewed = async (liveSessionId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/live/liveSession/${liveSessionId}/view`, `${this.config.verityOrigin}`),
        {
          method: "POST",
          credentials: "include"
        }
      );
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  endLiveSession = async (liveSessionId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/live/liveSession/${liveSessionId}/end`, `${this.config.verityOrigin}`),
        {
          method: "POST",
          credentials: "include"
        }
      );
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/platform-info/index.ts
import { platformInfoSchemaValidators as schemaValidators20 } from "@yujacorp/verity-api-contract/schemas/typebox";
var PlatformInfoClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators20);
  }
  current = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/platform-info`);
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.platformInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  isVersionSupported = async (version) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/platform-info/version/${version}`);
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.versionSupported.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/permission/index.ts
import { permissionSchemaValidators as schemaValidators21 } from "@yujacorp/verity-api-contract/schemas/typebox";
var PermissionClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators21);
  }
  doesUserHaveTestResultPermission = async (quizInfo) => {
    try {
      const resp = await super.fetch(
        new URL(
          `/api/verity/test-results/${quizInfo.lms}/${quizInfo.courseId ?? ""}/${quizInfo.quizId}?verify=permissions`,
          `${this.config.verityOrigin}`
        ),
        { credentials: "include" }
      );
      if (resp.status === 403) return false;
      const data = await resp.json();
      if (this.schemaValidators.verityResultPermission.validate(data)) {
        return data.verified;
      }
      return false;
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
      return false;
    }
  };
  // Same as above but only needs lmsCourseId and instituteId
  doesUserHaveInstructorPermission = async (lmsCourseId, instituteId) => handleApiErrorWithSchema(
    super.fetch(
      new URL(
        `${this.config.verityOrigin}/api/verity/test-results/${lmsCourseId}/instructor?instituteId=${instituteId}`
      ),
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.verityResultPermission
  );
  portalPermissions = async (lmsCourseId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/portal/permissions?lmsCourseId=${lmsCourseId}`, `${this.config.verityOrigin}`),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.portalPermission.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return [];
  };
  // TODO: get permission from access-control token
  fromHostname = async (info) => {
    try {
      const res = await super.fetch(
        new URL(`/api/verity/permissions/hostname/${info.hostname}`, `${this.config.verityOrigin}`)
      );
      if (res.ok) {
        const data = await res.json();
        if (this.schemaValidators.assessmentPermissions.validate(data)) {
          return data;
        }
      } else if (res.status === 401) {
        throw new UnauthenticatedError();
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return {
      assessment: []
    };
  };
  doesUserHaveInstituteProfilePermissions = async () => {
    try {
      const res = await super.fetch(
        new URL(`/api/verity/permissions/institute-profiles`, `${this.config.verityOrigin}`)
      );
      if (res.ok) {
        return true;
      } else if (res.status === 401) {
        throw new UnauthenticatedError();
      }
      return false;
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return false;
  };
  getUserPrivileges = () => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/access-control/user/self/privilege`, `${this.config.verityOrigin}`)),
    this.schemaValidators.scopedPrivileges
  );
};

// src/lms-info/index.ts
import { lmsInfoSchemaValidators as schemaValidators22 } from "@yujacorp/verity-api-contract/schemas/typebox";
var LmsInfoClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators22);
  }
  getAssessmentUrl = async (courseId, quizId, type) => {
    return handleApiErrorWithSchema(
      super.fetch(
        `${this.config.verityOrigin}/api/verity/lms-course/${courseId}/assessment/${quizId}/link?type=${type}`,
        { credentials: "include" }
      ),
      this.schemaValidators.url
    );
  };
  getCourseUrl = async (courseId, type) => {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/lms-course/${courseId}/link?type=${type}`, {
        credentials: "include"
      }),
      this.schemaValidators.url
    );
  };
  getInstituteLmsInfo = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/lms`, { credentials: "include" });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteLmsInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  createMoodleToken = async (accessToken) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/lms/moodle-token`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken })
      });
      if (response.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getTokenExists = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/lms/token`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.tokenExists.validate(obj)) {
          return obj.exists;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  deleteToken = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/lms/token`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getExactLmsVersion = async (instituteId) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/lms/version?instituteId=${instituteId}`,
        {
          method: "GET",
          credentials: "include"
        }
      );
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getLTIRef = async (instituteId) => {
    try {
      const resp = await super.fetch(new URL(`/api/verity/ltiRef/${instituteId}`, `${this.config.verityOrigin}`), {
        credentials: "include"
      });
      if (resp.ok) {
        const data = await resp.text();
        if (typeof data === "string") {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
  };
  getLTIRefByHostname = async (hostname) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/ltiRef/hostname/${hostname}`, `${this.config.verityOrigin}`),
        {
          credentials: "include"
        }
      );
      if (resp.ok) {
        const data = await resp.text();
        if (typeof data === "string") {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
  };
  getSessionToken = async (lmsCourseId) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/lms/session-token?lmsCourseId=${lmsCourseId}`,
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const token = (await response.json()).sessionToken;
        return token;
      }
    } catch (error) {
      console.error(error);
    }
  };
  testToken = async ({
    lms,
    category,
    lmsProvidedCourseId,
    accessToken
  }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/scope/${category}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lms,
          lmsProvidedCourseId,
          accessToken
        }),
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.tokenTestResults.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  createAssessment = async (lms, lmsCourseId) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/lms/${lms}/lms-course/${lmsCourseId}/lms-assessment`,
        {
          method: "POST",
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        return obj;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  updateAssessment = async (lms, lmsCourseId, lmsAssessmentId, accessCode) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/lms-course/${lmsCourseId}/lms-assessment/${lmsAssessmentId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lms, accessCode }),
          credentials: "include"
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  addQuestionToAssessment = async (lms, lmsCourseId, lmsAssessmentId, origin, question) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/lms-course/${lmsCourseId}/lms-assessment/${lmsAssessmentId}/question`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lms, origin, question }),
          credentials: "include"
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  deleteAssessment = async (assessmentId, deleteFromLms) => {
    try {
      const url = new URL(`${this.config.verityOrigin}/api/verity/assessment/${assessmentId}`);
      if (deleteFromLms) {
        url.searchParams.set("deleteFromLms", "true");
      }
      const response = await super.fetch(url, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.deleteResponse.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAllAssessmentInfoById = async (assessmentId) => {
    try {
      const resp = await super.fetch(new URL(`/api/verity/assessment/${assessmentId}`, `${this.config.verityOrigin}`), {
        credentials: "include"
      });
      if (resp.ok) {
        const obj = await resp.json();
        if (this.schemaValidators.deleteAssessmentInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAssessmentInfoById = async (assessmentId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/assessment/${assessmentId}/info`, `${this.config.verityOrigin}`),
        {
          credentials: "include"
        }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.assessmentInfoAlternate.validate(data)) {
          return {
            ...data,
            courseId: data.lmsProvidedCourseId,
            quizId: data.lmsProvidedAssessmentId
          };
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getApiIntegrationInfo = async ({ courseId, instituteId }) => handleApiErrorWithSchema(
    super.fetch(
      new URL(
        `/api/verity/lms/api-integration?instituteId=${instituteId}${courseId !== void 0 ? `&courseId=${courseId}` : ""}`,
        `${this.config.verityOrigin}`
      ),
      { credentials: "include" }
    ),
    this.schemaValidators.apiIntegrationInfo
  );
};

// src/download/index.ts
import { downloadSchemaValidators as schemaValidators23 } from "@yujacorp/verity-api-contract/schemas/typebox";
import { parse } from "yaml";
var DownloadClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators23);
  }
  getLockdownDownloadUrl = async (os, type) => {
    try {
      const response = await fetch(
        `${this.config.lockdown.origin}/${this.config.lockdown.channel}${os.startsWith("mac") ? "-mac" : ""}.yml`
      );
      if (response.ok) {
        const text = await response.text();
        if (typeof text === "string") {
          const appMeta = parse(text);
          if (this.schemaValidators.lockdownDownloadPath.validate(appMeta)) {
            let file;
            if (os === "mac-arm") {
              file = appMeta.files.find(({ url }) => url.endsWith("arm64.dmg"));
            } else if (os === "mac") {
              file = appMeta.files.find(({ url }) => !url.endsWith("arm64.dmg") && url.endsWith(".dmg"));
            } else {
              file = appMeta.files.find(({ url }) => url.endsWith(`.${type ?? "exe"}`));
            }
            return {
              url: `${this.config.lockdown.cdnBaseUrl}/${encodeURIComponent(file?.url ?? appMeta.path)}`,
              size: file?.size
            };
          }
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCandidateAiDownloadUrl = async (os, type) => {
    if (!this.config.candidateAi) {
      throw new ApiClientError(new Error("Candidate AI config is not set"));
    }
    try {
      const response = await fetch(
        `${this.config.lockdown.origin}/${this.config.candidateAi.channel}${os.startsWith("mac") ? "-mac" : ""}.yml`
      );
      if (response.ok) {
        const text = await response.text();
        if (typeof text === "string") {
          const appMeta = parse(text);
          if (this.schemaValidators.lockdownDownloadPath.validate(appMeta)) {
            let file;
            if (os === "mac-arm") {
              file = appMeta.files.find(({ url }) => url.endsWith("arm64.dmg"));
            } else if (os === "mac") {
              file = appMeta.files.find(({ url }) => !url.endsWith("arm64.dmg") && url.endsWith(".dmg"));
            } else {
              file = appMeta.files.find(({ url }) => url.endsWith(`.${type ?? "exe"}`));
            }
            return {
              url: `${this.config.lockdown.cdnBaseUrl}/${encodeURIComponent(file?.url ?? appMeta.path)}`,
              size: file?.size
            };
          }
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  requestProctoringSessionRecordingDownload = async (sessionId, userEnteredEmail, userEnteredName) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/recording/download-request/${sessionId}/${userEnteredEmail}/${userEnteredName}`,
        {
          method: "POST",
          credentials: "include"
        }
      );
      if (response.ok) {
        const details = await response.json();
        if (this.schemaValidators.requestProctoringSessionDownloadResponse.validate(details)) {
          return details;
        }
        throw new Error(`Invalid shape of response: ${JSON.stringify(details)}`);
      }
      throw new Error(`Invalid response, ${response.status}`);
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/verity-options/index.ts
import { verityOptionsSchemaValidators as schemaValidators24 } from "@yujacorp/verity-api-contract/schemas/typebox";
var VerityOptionsClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators24);
  }
  get = async ({
    isManagedGuestSession = false,
    ...quizInfo
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/options/${quizInfo?.lms ?? "verity"}/${quizInfo.hostname ?? location.hostname}/${quizInfo.courseId ?? ""}/${quizInfo.quizId}?isManagedGuestSession=${isManagedGuestSession}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.verityOptions.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  save = async ({
    quizInfo,
    courseInfo,
    options,
    ignore,
    standaloneOptions
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/options/${quizInfo?.lms ?? "verity"}/${quizInfo.hostname ?? location.hostname}/${quizInfo.courseId ?? ""}/${quizInfo.quizId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            quizInfo,
            courseInfo,
            verityOptions: options,
            ignore,
            standaloneOptions
          }),
          credentials: "include"
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAllApplications = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/applications/all`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.applicationResponse.validate(obj)) {
          const APPLICATION_LIST = obj.map((app) => ({
            id: app.id,
            name: app.name,
            processName: app.processName,
            iconSvg: app.iconSvg,
            type: app.type
          }));
          return APPLICATION_LIST;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAllExtensions = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/extensions/all`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.extensionResponse.validate(obj)) {
          const EXTENSION_LIST = obj.map((ext) => ({
            id: ext.extensionId,
            name: ext.extensionName,
            iconSvg: ext.iconSvg,
            type: ext.type
          }));
          return EXTENSION_LIST;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  fetchChromeExtensionData = async (id) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/extensions/webstore/${id}`, {
        credentials: "include"
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/institute/index.ts
import {
  instituteSchemaValidators as schemaValidators25
} from "@yujacorp/verity-api-contract/schemas/typebox";
var InstituteClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators25);
  }
  get = async (instituteId) => {
    try {
      if (!Number.isInteger(instituteId)) {
        throw new Error(`Invalid instituteId: ${instituteId}`);
      }
      const response = await super.fetch(new URL(`/api/verity/institute/${instituteId}`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccessControlId = async ({ subdomain }) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/access-control/institute/id?subdomain=${subdomain}`, this.config.verityOrigin), {
      credentials: "include"
    }),
    this.schemaValidators.accessControlInstituteId
  );
  isIntegrated = async (hostname) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute/hostname/${hostname}`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.integrated.validate(obj)) {
          return obj.integrated;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getLanguage = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/institute/language`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteLanguage.validate(obj)) {
          return obj.language;
        }
      } else if (response.status === 404) {
        return Promise.resolve("en");
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  isLiveEnabled = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v1/verity/settings`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.verityInstituteSettings.validate(obj)) {
          return !obj.liteMode && obj.allowLiveProctoring;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getVeritySettings = async (instituteId) => {
    try {
      const response = await super.fetch(
        new URL(
          instituteId == null ? `/api/verity/v1/verity/settings` : `/api/verity/v1/verity/institute/${instituteId}/settings`,
          this.config.verityOrigin
        ),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.verityInstituteSettings.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCourseSetting = async (lmsCourseId) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/portal/settings?lmsCourseId=${lmsCourseId}`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        obj["CourseSetting-allowStopRecording"] = obj?.["CourseSetting-allowStopRecording"] === "true";
        obj["InstituteSetting-allowStopRecording"] = obj?.["InstituteSetting-allowStopRecording"] === "true";
        obj["CourseEmailSetting-courseEmailSetting"] = obj?.["CourseEmailSetting-courseEmailSetting"] === "true";
        obj["InstituteEmailSetting-instituteEmailSetting"] = obj?.["InstituteEmailSetting-instituteEmailSetting"] === "true";
        if (this.schemaValidators.CourseSettings.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  uploadInstituteAgreement = async ({
    instituteId,
    fileName,
    extension,
    file,
    deleteFile
  }) => {
    try {
      const response = await super.fetch(
        `/api/verity/agreement/${instituteId}/${fileName}.${extension}${deleteFile ? "?delete=true" : ""}`,
        {
          method: "PUT",
          credentials: "include",
          body: file
        }
      );
      return response;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  deleteInstituteAgreement = async ({ instituteId }) => {
    try {
      const response = await super.fetch(`/api/verity/agreement/${instituteId}/delete`, {
        method: "PUT",
        credentials: "include"
      });
      return response;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  #convertDisabledProctoringOptions = (disabledProctoringOptionResponse) => {
    const validOptions = [];
    disabledProctoringOptionResponse?.forEach(
      (x) => this.schemaValidators.proctoringOptionKey.validate(x) && validOptions.push(x)
    );
    return validOptions;
  };
  getInstituteDisabledProctoringOptions = async () => this.#convertDisabledProctoringOptions(
    await handleApiErrorWithSchema(
      super.fetch(new URL(`/api/verity/institute-settings/disabled-options`, this.config.verityOrigin), {
        credentials: "include"
      }),
      this.schemaValidators.instituteDisabledProctoringOptions
    )
  );
  saveInstituteDisabledProctoringOptions = async (disabledProctoringOptions) => this.#convertDisabledProctoringOptions(
    await handleApiErrorWithSchema(
      super.fetch(new URL(`/api/verity/institute-settings/disabled-options`, this.config.verityOrigin), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ disabledProctoringOptions }),
        credentials: "include"
      }),
      this.schemaValidators.instituteDisabledProctoringOptions
    )
  );
  getInstituteThirdPartyPlatformsWithInstituteId = async (instituteId) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/${instituteId}/third-party-integrations`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        const selectedPlatformRoster = /* @__PURE__ */ new Map();
        const unselectedPlatformRoster = /* @__PURE__ */ new Map();
        if (this.schemaValidators.thirdPartyIntegrations.validate(obj)) {
          obj.selectedPlatforms.forEach((platform) => {
            selectedPlatformRoster.set(platform.platformId, platform);
          });
          obj.unselectedPlatforms.forEach((platform) => {
            unselectedPlatformRoster.set(platform.platformId, platform);
          });
        }
        return {
          selectedPlatformRoster: Object.fromEntries(selectedPlatformRoster),
          unselectedPlatformRoster: Object.fromEntries(unselectedPlatformRoster)
        };
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getInstituteThirdPartyPlatforms = async () => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/third-party-integrations`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        const selectedPlatformRoster = /* @__PURE__ */ new Map();
        const unselectedPlatformRoster = /* @__PURE__ */ new Map();
        if (this.schemaValidators.thirdPartyIntegrations.validate(obj)) {
          obj.selectedPlatforms.forEach((platform) => {
            selectedPlatformRoster.set(platform.platformId, platform);
          });
          obj.unselectedPlatforms.forEach((platform) => {
            unselectedPlatformRoster.set(platform.platformId, platform);
          });
        }
        return {
          selectedPlatformRoster: Object.fromEntries(selectedPlatformRoster),
          unselectedPlatformRoster: Object.fromEntries(unselectedPlatformRoster)
        };
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  saveInstituteThirdPartyPlatforms = async (thirdPartyPlatformRosters, rawNewlyEnabledThirdPartyPlatforms) => {
    try {
      if (!thirdPartyPlatformRosters) {
        return { ok: false, reason: "Failed to save third party integrations." };
      }
      const selectedPlatforms = Object.values(thirdPartyPlatformRosters.selectedPlatformRoster).map((platform) => ({
        platformId: platform.platformId,
        platformName: platform.platformName
      }));
      const unselectedPlatforms = Object.values(thirdPartyPlatformRosters.unselectedPlatformRoster).map((platform) => ({
        platformId: platform.platformId,
        platformName: platform.platformName
      }));
      const serializableRosters = {
        unselectedPlatforms,
        selectedPlatforms
      };
      let newlyEnabledThirdPartyPlatforms = void 0;
      if (rawNewlyEnabledThirdPartyPlatforms && rawNewlyEnabledThirdPartyPlatforms.length > 0) {
        const mapToBackendPlatform = (p) => ({
          name: p.platformName,
          id: p.platformId
        });
        newlyEnabledThirdPartyPlatforms = rawNewlyEnabledThirdPartyPlatforms.map(mapToBackendPlatform);
      }
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/third-party-integrations`, this.config.verityOrigin),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            thirdPartyPlatformRosters: serializableRosters,
            newlyEnabledThirdPartyPlatforms
          }),
          credentials: "include"
        }
      );
      if (response.ok) {
        return { ok: true };
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getInstituteAllowStrictMode = async () => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/allow-strict-mode`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteAllowStrictMode.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  saveInstituteAllowStrictMode = async (allowStrictMode) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/allow-strict-mode`, this.config.verityOrigin),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ allowStrictMode }),
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteAllowStrictMode.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getInstituteScopeSettings = async () => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/scope-mode`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteScopeMode.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  saveInstituteScopeSettings = async (scopeSettingsData) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/scope-mode`, this.config.verityOrigin),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ scopeSettingsData }),
          credentials: "include"
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getInstituteRules = async (instituteUuid) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/verity/rule/${instituteUuid}`, this.config.verityOrigin), {
      credentials: "include"
    }),
    this.schemaValidators.rulesResponse
  );
  updateInstituteRules = async ({
    instituteUuid,
    ...rules
  }) => handleApiError(
    super.fetch(new URL(`/api/verity/rule/${instituteUuid}`, this.config.verityOrigin), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(rules)
    })
  );
  syncLmsAccounts = async () => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/sync-accounts`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getInstituteIdFromUuid = async (uuid) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/institute/uuid:${uuid}`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteId.validate(obj)) {
          return obj.id;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccessControlInfo = async (uuid) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/institute/${uuid}`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.accessControlInstitute.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  updateAccessControlInfo = async ({
    instituteUuid,
    origin
  }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/institute/${instituteUuid}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // name,
          origin
          // platform,
        }),
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.accessControlInstitute.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getApiKeys = async (uuid) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/institute/${uuid}/api-key`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.apiKeys.validate(obj)) {
          return obj.apiKeys;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  createApiKey = async ({
    instituteUuid,
    keyId,
    keyValue,
    platform,
    origin,
    apiUrl
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/institute/${instituteUuid}/api-key`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            keyId,
            keyValue,
            platform,
            origin,
            apiUrl
          }),
          credentials: "include"
        }
      );
      if (response.ok) {
        return;
      } else {
        throw new Error(`${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  updateApiKey = async ({
    instituteUuid,
    id,
    platform,
    origin,
    apiUrl
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/institute/${instituteUuid}/api-key/${id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            platform,
            origin,
            apiUrl
          }),
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.apiKey.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  deleteApiKey = async ({ instituteUuid, id }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/institute/${instituteUuid}/api-key/${id}`,
        {
          method: "DELETE",
          credentials: "include"
        }
      );
      if (response.ok) {
        return;
      } else {
        throw new Error(`${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  logInstituteUpdate = async ({
    type,
    payload,
    details,
    timestamp
  }) => {
    try {
      await super.fetch(`${this.config.verityOrigin}/api/verity/debug/institute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          details: {
            type,
            payload,
            ...details
          },
          timestamp
        })
      });
      return;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getSuffix = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/institute-settings/suffix`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteSuffix.validate(obj)) {
          return obj.suffix;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  saveSuffix = async (suffix) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/institute-settings/suffix`, this.config.verityOrigin), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ suffix }),
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteSuffix.validate(obj)) {
          return obj.suffix;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  isVerityEnabled = async (courseId) => {
    return (await handleApiErrorWithSchema(
      super.fetch(new URL(`/api/verity/institute-settings/is-verity-enabled/${courseId}`, this.config.verityOrigin), {
        credentials: "include"
      }),
      this.schemaValidators.verityEnabled
    )).verityEnabled;
  };
  #convertInstituteSettings = (instituteSettingsResponse) => {
    return {
      ...instituteSettingsResponse,
      disabledProctoringOptions: this.#convertDisabledProctoringOptions(
        instituteSettingsResponse.disabledProctoringOptions
      )
    };
  };
  getSettings = async (instituteUuid) => this.#convertInstituteSettings(
    await handleApiErrorWithSchema(
      super.fetch(new URL(`/api/verity/institute/${instituteUuid}/settings`, this.config.verityOrigin), {
        credentials: "include"
      }),
      this.schemaValidators.instituteSettings
    )
  );
  updateSettings = async (instituteUuid, settings) => this.#convertInstituteSettings(
    await handleApiErrorWithSchema(
      super.fetch(new URL(`/api/verity/institute/${instituteUuid}/settings`, this.config.verityOrigin), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings)
      }),
      this.schemaValidators.instituteSettings
    )
  );
  setMfaRequired = async (instituteUuid, mfaRequired) => {
    await handleApiError(
      super.fetch(
        new URL(
          `/api/access-control/institute/${instituteUuid}/mfa?mfaRequired=${mfaRequired}`,
          this.config.verityOrigin
        ),
        {
          method: "PATCH",
          credentials: "include"
        }
      )
    );
  };
  getLiveProctoringUsage = async (instituteUuid) => {
    return (await handleApiErrorWithSchema(
      super.fetch(
        new URL(`/api/verity/usage/live-proctoring?instituteId=${instituteUuid}`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      ),
      this.schemaValidators.usageHours
    )).hours;
  };
  getLivePopInUsage = async (instituteUuid) => {
    return (await handleApiErrorWithSchema(
      super.fetch(new URL(`/api/verity/usage/live-pop-in?instituteId=${instituteUuid}`, this.config.verityOrigin), {
        credentials: "include"
      }),
      this.schemaValidators.usageHours
    )).hours;
  };
  //#region Hostname
  /**
   * Retrieves the list of hostnames associated with the given institute.
   *
   * @param {string} instituteUuid - The Database ID of the institute.
   *
   * @returns {Promise<string[]>} - A promise that resolves with an array of hostnames.
   */
  getHostnames = async ({ instituteUuid }) => {
    try {
      const response = await super.fetch(
        new URL(`/api/access-control/institute/${instituteUuid}/hostname`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (!response.ok) {
        throw new ApiClientError(Error(`Invalid response: ${response.statusText}`));
      }
      const obj = await response.json();
      const isValidObject = this.schemaValidators.hostnames.validate(obj);
      if (!isValidObject) {
        throw new ApiClientError(Error(`Invalid object: ${JSON.stringify(obj)}`));
      }
      return obj;
    } catch (error) {
      throw new ApiClientError(Error(`Caught Error: ${String(error)}`));
    }
  };
  /**
   * Adds a new hostname to the given institute.
   *
   * @param {string} instituteUuid - The Database ID of the institute.
   * @param {string} hostname - The hostname to be added.
   *
   * @returns {Promise<string>} - A promise that resolves when the hostname is successfully added.
   */
  addHostname = async ({ instituteUuid, hostname }) => {
    try {
      const response = await super.fetch(
        new URL(`/api/access-control/institute/${instituteUuid}/hostname/${hostname}`, this.config.verityOrigin),
        {
          method: "POST",
          credentials: "include"
        }
      );
      if (!response.ok) {
        throw new ApiClientError(Error(`Invalid response: ${response.statusText}`));
      }
      return hostname;
    } catch (error) {
      throw new ApiClientError(Error(`Caught Error: ${String(error)}`));
    }
  };
  /**
   * Removes a hostname from the given institute.
   *
   * @param {number} instituteUuid - The Database ID of the institute.
   * @param {string} hostname - The hostname to be removed.
   *
   * @returns {Promise<string>} - A promise that resolves when the hostname is successfully removed.
   */
  removeHostname = async ({ instituteUuid, hostname }) => {
    try {
      const response = await super.fetch(
        new URL(`/api/access-control/institute/${instituteUuid}/hostname/${hostname}`, this.config.verityOrigin),
        {
          method: "DELETE",
          credentials: "include"
        }
      );
      if (!response.ok) {
        throw new ApiClientError(Error(`Invalid response: ${response.statusText}`));
      }
      return hostname;
    } catch (error) {
      throw new ApiClientError(Error(`Caught Error: ${String(error)}`));
    }
  };
  getInstitutionProctoringAccommodation = async () => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/proctoring-accommodations`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteProctoringAccommodation.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  updateInstitutionProctoringAccommodation = async (proctoringAccommodations) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/proctoring-accommodations`, this.config.verityOrigin),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ proctoringAccommodations }),
          credentials: "include"
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getPolicyPdfFromInstituteId = async (instituteId) => {
    try {
      const url = new URL(`/api/verity/agreement/${instituteId}/url`, this.config.verityOrigin);
      const response = await super.fetch(url, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.agreementUrl.validate(obj)) {
          return obj.url;
        }
      } else {
        return void 0;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  //#endregion
};

// src/user/index.ts
import { userSchemaValidators as schemaValidators26 } from "@yujacorp/verity-api-contract/schemas/typebox";
var UserClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators26);
  }
  getInstituteStudents = async () => {
    try {
      const response = await super.fetch(new URL("/api/verity/user/student-roster", this.config.verityOrigin), {
        credentials: "include"
      });
      const obj = await response.json();
      if (this.schemaValidators.students.validate(obj)) {
        return obj;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCourseStudents = async (assessmentInfo) => {
    if (assessmentInfo.courseId) {
      try {
        const response = await super.fetch(
          new URL(`/api/verity/user/student-roster/${assessmentInfo.courseId}`, this.config.verityOrigin),
          {
            credentials: "include"
          }
        );
        if (response.ok) {
          const obj = await response.json();
          if (this.schemaValidators.students.validate(obj)) {
            return obj;
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          throw new ApiClientError(error);
        }
      }
    } else {
      console.error("No courseId provided");
    }
    throw new ApiClientError();
  };
  getProctoringExemptionStatus = async (assessmentInfo) => {
    try {
      const response = await super.fetch(
        new URL(
          `/api/verity/proctoring-exemption/lms-course/${assessmentInfo.courseId}/lms-assessment/${assessmentInfo.quizId}/user`,
          this.config.verityOrigin
        ),
        { credentials: "include" }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.exemptionStatus.validate(obj)) {
          return obj;
        }
      }
      throw new Error("ExemptionStatus was not type {exempt: boolean}");
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError(new Error(`Unable to fetch exemption status`));
  };
  getProctoringExemptionStudents = async (assessmentInfo) => {
    if (assessmentInfo.courseId) {
      try {
        const response = await super.fetch(
          new URL(
            `/api/verity/proctoring-exemption/lms-course/${assessmentInfo.courseId}/lms-assessment/${assessmentInfo.quizId}`,
            this.config.verityOrigin
          ),
          { credentials: "include" }
        );
        if (response.ok) {
          const obj = await response.json();
          if (this.schemaValidators.proctoringExemptions.validate(obj)) {
            return obj;
          }
        } else if (response.status === 404) {
          return [];
        }
      } catch (error) {
        if (error instanceof Error) {
          throw new ApiClientError(error);
        }
      }
    } else {
      console.error("No courseId provided");
    }
    throw new ApiClientError();
  };
  saveProctoringExemptionStudents = async ({
    assessmentInfo,
    students
  }) => {
    try {
      const response = await super.fetch(
        new URL(
          `/api/verity/proctoring-exemption/lms-course/${assessmentInfo.courseId}/lms-assessment/${assessmentInfo.quizId}`,
          this.config.verityOrigin
        ),
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            exemptedStudents: students
          })
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getExemptionInfoSuccessStatus = async (assessmentInfo) => {
    if (assessmentInfo.courseId) {
      try {
        const response = await super.fetch(
          new URL(
            `/api/verity/lms-course/${assessmentInfo.courseId}/lms-assessment/${assessmentInfo.quizId}`,
            this.config.verityOrigin
          ),
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json"
            }
          }
        );
        return response.ok;
      } catch (error) {
        return false;
      }
    } else {
      return false;
    }
  };
  createStandaloneUser = ({
    instituteUuid,
    courseUuid,
    assessmentUuid,
    email,
    firstName,
    lastName,
    expiryDate,
    emailTemplateData
  }) => {
    return handleApiErrorWithSchema(
      super.fetch(new URL("/api/access-control/single-use-account", this.config.verityOrigin), {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          instituteId: instituteUuid,
          courseId: courseUuid,
          resourceId: assessmentUuid,
          email,
          firstName,
          lastName,
          expiryDate,
          emailTemplateData
        })
      }),
      this.schemaValidators.standaloneAccount
    );
  };
  getUserId = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/user/id`, this.config.verityOrigin), {
        credentials: "include"
      });
      const userId = await response.json();
      if (this.schemaValidators.studentId.validate(userId)) {
        return userId.id;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getUserInfo = async (userId) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-user${userId != null ? `/${userId}` : ""}`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      const userInfo = await response.json();
      return userInfo;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccessControlUserInfo = async (userId) => {
    try {
      const response = await super.fetch(new URL(`/api/access-control/user/${userId}`, this.config.verityOrigin));
      const userInfo = await response.json();
      if (this.schemaValidators.accessControlUserInfo.validate(userInfo)) {
        return userInfo;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getUserLanguage = async (userId) => {
    try {
      const url = new URL(`/api/verity/user/${userId}/language`, this.config.verityOrigin);
      const response = await super.fetch(url, {
        credentials: "include"
      });
      const language = await response.json();
      if (this.schemaValidators.language.validate(language)) {
        return language;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  saveUserLanguage = async (userId, language) => {
    try {
      const url = new URL(`/api/verity/user/${userId}/language`, this.config.verityOrigin);
      const response = await super.fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          language
        })
      });
      return response.status;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  /**
   * Get the best available launch URL it is one of:
   * - This sessions institute origin
   * - The last course's institute origin
   * - The last course's URL
   * - The external tool URL in the last course
   */
  getLaunchUrl = async () => {
    try {
      const url = new URL(`/api/verity/user/launch`, this.config.verityOrigin);
      const response = await super.fetch(url, {
        credentials: "include"
      });
      if (response.ok) {
        const json = await response.json();
        return json;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getNotifications = async ({ instituteUuid, courseUuid }) => {
    const searchParams = new URLSearchParams();
    if (instituteUuid) {
      searchParams.set("instituteId", instituteUuid);
    }
    if (courseUuid) {
      searchParams.set("courseId", courseUuid);
    }
    return await handleApiErrorWithSchema(
      super.fetch("/api/verity/user/notification" + (searchParams.size > 0 ? `?${searchParams.toString()}` : ""), {
        credentials: "include"
      }),
      this.schemaValidators.notifications
    );
  };
  dismissNotifications = ({ type }) => {
    const searchParams = new URLSearchParams();
    if (type) {
      searchParams.set("type", type);
    }
    return handleApiError(
      super.fetch(`/api/verity/user/notification?${searchParams.toString()}`, {
        method: "DELETE",
        credentials: "include"
      })
    );
  };
  updateLoginCount = () => {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/user/login-count`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      }),
      this.schemaValidators.loginCount
    );
  };
  getHasShownCandidateWelcome = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/candidate/has-shown-welcome`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const hasBeenShown = await response.json();
        return !!hasBeenShown.candidateWelcomeShownAt;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  postHasShownCandidateWelcome = async () => {
    return handleApiErrorWithSchema(
      super.fetch(new URL(`/api/verity/candidate/has-shown-welcome`, this.config.verityOrigin), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        }
      }),
      this.schemaValidators.hasShownWelcome
    );
  };
  assignInterviewerRole = async ({ courseId }) => {
    const response = await super.fetch(
      new URL(`/api/verity/candidate/assign-interviewer-roles/${courseId}`, this.config.verityOrigin),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      }
    );
    return response.ok;
  };
  mapAccessControlInfo = () => handleApiError(super.fetch(`${this.config.verityOrigin}/api/verity/user/map`, { method: "POST" }));
};

// src/start-assessment/index.ts
import { startAssessmentSchemaValidators as schemaValidators27 } from "@yujacorp/verity-api-contract/schemas/typebox";
var startAssessmentClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators27);
  }
  check = async (data) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/quiz/take/check`, this.config.verityOrigin), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          data
        })
      });
      if (response.ok) {
        const responseData = await response.json();
        if (this.schemaValidators.checkUserAndAuthenticateResponse.validate(responseData)) {
          return responseData;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  start = async (assessmentInfo) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/quiz/take/check`, this.config.verityOrigin), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          assessmentInfo
        })
      });
      if (response.ok) {
        const responseData = await response.json();
        if (this.schemaValidators.checkUserAndAuthenticateResponse.validate(responseData)) {
          return responseData;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getUploadContext = async (proctoringSessionId) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/verity/quiz/take/upload-context/${proctoringSessionId}`, this.config.verityOrigin), {
      credentials: "include"
    }),
    this.schemaValidators.getUploadContextResponse
  );
};

// src/system-check/index.ts
import { systemCheckSchemaValidators as schemaValidators28 } from "@yujacorp/verity-api-contract/schemas/typebox";
var SystemCheckClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators28);
  }
  // Returns upload speed in bits per second
  async #badConnectionCheck(uploadByteSize = 8e5, timeout = 5e3, numFetchRequest = 8) {
    if (globalThis.XMLHttpRequest) {
      const data = new Blob(["0".repeat(uploadByteSize)]);
      return new Promise((resolve, _reject) => {
        const xhr = new XMLHttpRequest();
        const connectionCheckUrl = `${this.config.verityOrigin}/connection/check`;
        let startTime;
        let lastLoaded = 0;
        xhr.open("POST", connectionCheckUrl, true);
        xhr.timeout = timeout;
        xhr.upload.addEventListener("loadstart", function(ev) {
          if (ev.loaded === 0) {
            startTime = performance.now();
          }
        });
        xhr.upload.addEventListener("progress", (ev) => {
          if (ev.lengthComputable) {
            lastLoaded = ev.loaded;
          }
        });
        const finalize = () => {
          const duration = performance.now() - startTime;
          resolve(lastLoaded * 8 / (duration / 1e3));
        };
        xhr.upload.addEventListener("loadend", finalize);
        xhr.ontimeout = finalize;
        xhr.send(data);
      });
    } else {
      const controller = new AbortController();
      const startTime = performance.now();
      let lastFetchEndTime = startTime;
      let lastFetchDuration = 0;
      const smallByteSize = uploadByteSize / numFetchRequest;
      let uploadSuccessCount = 0;
      const data = new Blob(["0".repeat(smallByteSize)]);
      setTimeout(() => {
        controller.abort();
      }, timeout);
      try {
        for (let i = 0; i < numFetchRequest; i++) {
          const fetchStart = performance.now();
          const resp = await this.fetch(`${this.config.verityOrigin}/api/verity/connection/check`, {
            method: "POST",
            body: data,
            signal: controller.signal
          });
          if (resp.ok) {
            lastFetchEndTime = performance.now();
            lastFetchDuration = lastFetchEndTime - fetchStart;
            uploadSuccessCount++;
          }
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          throw err;
        }
      }
      const endTime = performance.now();
      const remainingTime = endTime - lastFetchEndTime;
      const partialRatio = lastFetchDuration === 0 ? 0 : remainingTime / lastFetchDuration;
      const uploadedBytes = uploadSuccessCount * smallByteSize + partialRatio * smallByteSize;
      return uploadedBytes * 8 / ((endTime - startTime) / 1e3);
    }
  }
  /**
   * This connection check sucks
   */
  checkConnection = async (uploadByteSize, timeout, numFetchRequest) => {
    return {
      metrics: {
        //@ts-ignore
        type: navigator.connection?.type,
        //@ts-ignore
        effectiveType: navigator.connection?.effectiveType,
        //@ts-ignore
        downlinkMax: navigator.connection?.downlinkMax,
        //@ts-ignore
        downlink: navigator.connection?.downlink,
        //@ts-ignore
        rtt: navigator.connection?.rtt
      },
      uploadSpeed: await this.#badConnectionCheck(uploadByteSize, timeout, numFetchRequest)
    };
  };
};

// src/course/index.ts
import {
  courseSchemaValidators as schemaValidators29
} from "@yujacorp/verity-api-contract/schemas/typebox";
var CourseClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators29);
  }
  getCourseInfoFromId = async (id) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/course/${id}`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.courseInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCourseInfoFromLmsCourseId = async ({
    lmsCourseId,
    instituteId
  }) => {
    try {
      const url = new URL(`${this.config.verityOrigin}/api/verity/course/lms-course/${lmsCourseId}`);
      if (instituteId) {
        url.searchParams.set("instituteId", instituteId.toString());
      }
      const response = await super.fetch(url, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.courseInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCourseIdFromUuid = async (uuid) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/course/uuid:${uuid}`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.courseId.validate(obj)) {
          return obj.id;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getInstituteCoursesInfo = async (instituteId) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/course/institute/${instituteId}`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.courseInfoList.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccessControlCourse = async (courseUuid) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/course/${courseUuid}`);
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.accessControlCourseInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCourseCountForPrefix = async (instituteUuid, namePrefix) => {
    try {
      const searchParams = new URLSearchParams();
      searchParams.set("instituteId", instituteUuid);
      searchParams.set("name", namePrefix);
      searchParams.set("matchNameMode", "STARTS_WITH");
      searchParams.set("ignoreCase", "0");
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/course?${searchParams.toString()}`
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.courseCount.validate(obj)) {
          return obj.totalElements;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCourseSettings = async (verityCourseId) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/verity/course-settings/${verityCourseId}`, this.config.verityOrigin), {
      credentials: "include"
    }),
    this.schemaValidators.courseSettings
  );
  updateCourseSettings = async (verityCourseId, settings) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/verity/course-settings/${verityCourseId}`, this.config.verityOrigin), {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings)
    }),
    this.schemaValidators.courseSettings
  );
  getCourseRootModules = async (courseId) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/verity/course/${courseId}/root-modules`, this.config.verityOrigin), {
      credentials: "include"
    }),
    this.schemaValidators.courseRootModules
  );
  createCourseRootModule = async (courseId, moduleName) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/verity/course/${courseId}/root-modules`, this.config.verityOrigin), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ moduleName })
    }),
    this.schemaValidators.module
  );
  getCourseRootSections = async (courseId) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/verity/course/${courseId}/root-sections`, this.config.verityOrigin), {
      credentials: "include"
    }),
    this.schemaValidators.courseRootSections
  );
  createCourseRootSection = async (courseId, sectionName) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/verity/course/${courseId}/root-sections`, this.config.verityOrigin), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sectionName })
    }),
    this.schemaValidators.section
  );
};

// src/proctoring-configuration/index.ts
import { proctoringConfigurationSchemaValidators as schemaValidators30 } from "@yujacorp/verity-api-contract/schemas/typebox";
var ProctoringConfigurationClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators30);
  }
  get = async (assessmentInfo) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/assessment/configuration/${assessmentInfo?.lms ?? "verity"}/${assessmentInfo.hostname ?? location.hostname}/${assessmentInfo.courseId ?? ""}/${assessmentInfo.quizId}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.proctoringAssessmentConfigurationV2OrNull.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  save = async ({
    assessmentInfo,
    courseInfo,
    profileId,
    proctoringAssessmentConfiguration,
    proctoringOption,
    standaloneOptions,
    verityAssessmentOptions,
    quizQuestions
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/assessment/configuration/${assessmentInfo.lms ?? "verity"}/${assessmentInfo.hostname ?? location.hostname}/${assessmentInfo.courseId ?? ""}/${assessmentInfo.quizId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            assessmentInfo,
            courseInfo,
            profileId,
            proctoringAssessmentConfiguration,
            proctoringOption,
            standaloneOptions,
            verityAssessmentOptions,
            quizQuestions
          }),
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.proctoringAssessmentConfigurationResponse.validate(obj)) {
          return obj;
        }
      }
      if (response.status === 413) {
        const payloadError = new Error("Payload too large");
        throw new ApiClientError(payloadError);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  updateWhitelist = async ({ courseId, quizId, type }) => {
    try {
      let url = `${this.config.verityOrigin}/api/verity/assessment/update-whitelist/${courseId}/${quizId}`;
      if (type) {
        url += `?type=${type}`;
      }
      await super.fetch(url, {
        method: "PUT",
        credentials: "include"
      });
    } catch (error) {
      console.error(error);
    }
    return;
  };
  getEnabledOptions = ({
    assessmentInfo,
    courseName
  }) => {
    let url = `${this.config.verityOrigin}/api/verity/assessment/configuration/${assessmentInfo?.lms ?? "verity"}/${assessmentInfo.hostname ?? location.hostname}/${assessmentInfo.courseId ?? ""}/enabled-options`;
    if (courseName) {
      url += `?courseName=${encodeURIComponent(courseName)}`;
    }
    return handleApiErrorWithSchema(
      super.fetch(url, {
        credentials: "include"
      }),
      this.schemaValidators.enabledOptionsResponse
    );
  };
  getAssessmentResourceUploadUrl = ({
    quizId,
    mimeType,
    fileName,
    fileSize
  }) => {
    const baseUrl = `${this.config.verityOrigin}/api/verity/assessment/configuration/${quizId}/assessment-resource-url`;
    const params = new URLSearchParams({
      mimeType,
      fileName,
      fileSize: fileSize.toString()
    });
    const url = `${baseUrl}?${params.toString()}`;
    return handleApiErrorWithSchema(
      super.fetch(url, {
        credentials: "include"
      }),
      this.schemaValidators.assessmentResourceUploadUrlResponse
    );
  };
  deleteAssessmentResource = ({ quizId, fileName }) => {
    const params = new URLSearchParams({
      fileName
    });
    return handleApiError(
      super.fetch(
        `${this.config.verityOrigin}/api/verity/assessment/configuration/${quizId}/assessment-resource-url?${params.toString()}`,
        {
          method: "DELETE",
          credentials: "include"
        }
      )
    );
  };
  getAssessmentResources = (quizId) => {
    const url = `${this.config.verityOrigin}/api/verity/assessment/configuration/${quizId}/assessment-resources`;
    return handleApiErrorWithSchema(
      super.fetch(url, {
        credentials: "include"
      }),
      this.schemaValidators.getAssessmentResourcesResponse
    );
  };
  publishVerityAssessment = async ({ quizId, isPublished }) => handleApiError(
    super.fetch(`${this.config.verityOrigin}/api/verity/assessment/configuration/${quizId}/publish`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ isPublished }),
      credentials: "include"
    })
  );
};

// src/proctoring-profile/index.ts
import { proctoringProfileSchemaValidators as schemaValidators31 } from "@yujacorp/verity-api-contract/schemas/typebox";
var ProctoringProfileClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators31);
  }
  getAll = async ({ lmsProvidedCourseId }) => {
    try {
      const url = new URL(`/api/verity/v2/profile`, this.config.verityOrigin);
      if (lmsProvidedCourseId) {
        url.searchParams.set("lmsProvidedCourseId", lmsProvidedCourseId);
      }
      const response = await super.fetch(url, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.profiles.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  create = async ({
    proctoringOption,
    isInstituteProfile,
    lmsProvidedCourseId,
    name
  }) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profile`, this.config.verityOrigin), {
        headers: {
          "Content-Type": "application/json"
        },
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          proctoringOption,
          isInstituteProfile,
          lmsProvidedCourseId,
          name
        })
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.profile.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  update = async ({
    id,
    proctoringOption,
    name
  }) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profile/${id}`, this.config.verityOrigin), {
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        method: "PATCH",
        body: JSON.stringify({
          proctoringOption,
          name
        })
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.profile.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  delete = async (id) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profile/${id}`, this.config.verityOrigin), {
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        method: "DELETE"
      });
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  enforce = async ({
    enforceProfileIds,
    unenforceProfileIds
  }) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profiles/enforce`, this.config.verityOrigin), {
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        method: "PATCH",
        body: JSON.stringify({
          enforceProfileIds,
          unenforceProfileIds
        })
      });
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  profilesEnforced = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profiles/enforce`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.enforced.validate(obj)) {
          return obj.enforced;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getDefault = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profile/default`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.profileOrNull.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  setDefault = async (proctoringOptionProfileId) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profile/default`, this.config.verityOrigin), {
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        method: "PATCH",
        body: JSON.stringify({
          proctoringOptionProfileId
        })
      });
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getPreset = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profile/preset`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.profiles.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/assessment/index.ts
import {
  assessmentSchemaValidators as schemaValidators32
} from "@yujacorp/verity-api-contract/schemas/typebox";
var AssessmentClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators32);
  }
  getAssessments = async ({ course }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/course/${course.id}/assessments`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.assessmentInfoArray.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    return [];
  };
  useAssessmentsWithTypeCount = async ({
    instituteId,
    courseId,
    time
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/institute/${instituteId}/courseId/${courseId}/time/${time}`,
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.assessmentMonthlyCountList.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
  };
  getCourseAssessments = async (lmsCourseId) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/lms-course/${lmsCourseId}/assessments`,
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.assessmentInfoList.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    return [];
  };
  getVerityEnabledAssessments = async (instituteId, lmsCourseId) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/lms-course/${lmsCourseId}/verity-enabled-assessments?instituteId=${instituteId}`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.verityEnabledAssessments
  );
  getAssessmentByCourseQuizId = async ({ lmsQuizId, courseId }) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/course/${courseId}/lms-assessment/${lmsQuizId}`, `${this.config.verityOrigin}`),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.assessmentInfo.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
  };
  getAssessmentByAssessmentInfo = async (assessmentInfo) => {
    try {
      const resp = await super.fetch(
        new URL(
          `/api/verity/lms-course/${assessmentInfo.courseId}/lms-assessment/${assessmentInfo.quizId}`,
          `${this.config.verityOrigin}`
        ),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.assessmentInfo.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
  };
  getAssessmentByAssessmentId = async (assessmentId) => {
    try {
      const resp = await super.fetch(new URL(`/api/verity/assessment/${assessmentId}`, `${this.config.verityOrigin}`), {
        credentials: "include"
      });
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.assessmentInfo.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
  };
  isAssessmentVerityEnabled = async (assessmentInfo) => {
    try {
      const searchParams = new URLSearchParams();
      if (assessmentInfo.instituteId) {
        searchParams.append("instituteId", assessmentInfo.instituteId.toString());
      }
      if (assessmentInfo.hostname) {
        searchParams.append("hostname", assessmentInfo.hostname);
      }
      const resp = await super.fetch(
        new URL(
          `/api/verity/lms-course/${assessmentInfo.courseId}/lms-assessment/${assessmentInfo.quizId}/proctored?${searchParams.toString()}`,
          `${this.config.verityOrigin}`
        ),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (typeof data === "boolean") {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  assessmentCopyCheck = async (assessmentInfo) => {
    try {
      const type = assessmentInfo.isCanvasNewQuiz ? "canvas-new-quiz" : assessmentInfo.assessmentType ?? void 0;
      const searchParams = new URLSearchParams();
      if (assessmentInfo.instituteId) {
        searchParams.append("instituteId", assessmentInfo.instituteId.toString());
      }
      if (assessmentInfo?.hostname) {
        searchParams.append("hostname", assessmentInfo.hostname);
      }
      if (type) {
        searchParams.append("type", type);
      }
      const resp = await super.fetch(
        new URL(
          `/api/verity/lms-course/${assessmentInfo.courseId}/lms-assessment/${assessmentInfo.quizId}/copy-check?${searchParams.toString()}`,
          `${this.config.verityOrigin}`
        ),
        { method: "PUT", credentials: "include" }
      );
      if (resp.ok) {
        return;
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getStandaloneStudents = async (assessmentId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/standalone-api/assessment/${assessmentId}/students`, `${this.config.verityOrigin}`),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.standaloneStudentList.validate(data)) {
          return data;
        }
        throw new Error(`Invalid shape of response`);
      }
      throw new Error(`Invalid response: ${resp.status} - ${resp.statusText}`);
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getStandaloneStudentName = async (standaloneUserId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/standalone-api/name/${standaloneUserId}`, `${this.config.verityOrigin}`),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        return data;
      }
      throw new Error(`Invalid response: ${resp.status} - ${resp.statusText}`);
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  linkStandaloneStudentUser = ({
    assessmentId,
    standaloneStudentId,
    accountUuid,
    userUuid
  }) => {
    return handleApiError(
      super.fetch(
        new URL(
          `/api/verity/standalone-api/assessment/${assessmentId}/student/${standaloneStudentId}/map`,
          `${this.config.verityOrigin}`
        ),
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accountUuid, userUuid })
        }
      )
    );
  };
  deleteStandaloneAssessmentByUUID = async (assessmentUUID, courseId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/standalone-api/assessment/${assessmentUUID}/${courseId}`, `${this.config.verityOrigin}`),
        { method: "DELETE", credentials: "include" }
      );
      if (resp.ok) {
        return { success: true };
      }
      throw new Error(`Invalid response: ${resp.status} - ${resp.statusText}`);
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  updateLmsAssessmentNameSuffix = async ({
    assessmentId,
    oldSuffix,
    newSuffix
  }) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/assessment/${assessmentId}/suffix`, `${this.config.verityOrigin}`),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            oldSuffix,
            newSuffix
          })
        }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.suffixUpdateResult.validate(data)) {
          return data;
        }
        throw new Error(`Invalid shape of response`);
      }
      throw new Error(`Invalid response: ${resp.status} - ${resp.statusText}`);
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  /**
   * Validate the assessments configuration against the LMS and
   * return the results
   */
  validate = async ({ courseId, quizId }) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/lms-course/${courseId}/lms-assessment/${quizId}/validate`, `${this.config.verityOrigin}`),
        {
          method: "GET",
          headers: { "Content-Type": "application/json" }
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (this.schemaValidators.validate.validate(data)) {
          return data;
        }
        throw new Error(`Invalid shape of response`);
      }
      throw new Error(`Invalid response: ${response.status} - ${response.statusText}`);
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  /**
   * Synchronize the given assessment's password between the
   * LMS and our database if needed
   */
  synchronize = async ({ courseId, quizId }) => {
    try {
      const response = await super.fetch(
        new URL(
          `/api/verity/lms-course/${courseId}/lms-assessment/${quizId}/synchronize`,
          `${this.config.verityOrigin}`
        ),
        {
          method: "GET",
          headers: { "Content-Type": "application/json" }
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (this.schemaValidators.synchronize.validate(data)) {
          return data;
        }
        throw new Error(`Invalid shape of response`);
      }
      throw new Error(`Invalid response: ${response.status} - ${response.statusText}`);
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getContentQuizThirdPartyPlatform = async (instituteId, courseId, quizId) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/institute/${instituteId}/courseId/${courseId}/quizId/${quizId}/third-party-platform`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.d2lContentPlatform
  );
  getVerityThirdPartyQuizzes = async (instituteId, courseId) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/lms-course/${courseId}/verity-third-party-quizzes?instituteId=${instituteId}`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.lmsThirdPartyQuizzes
  );
  getQuizzesFromCache = async (instituteId, courseId, lmsAssessmentIds) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/lms-course/${courseId}/check-quiz-cache?instituteId=${instituteId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          quizIds: lmsAssessmentIds
        })
      }
    ),
    this.schemaValidators.lmsThirdPartyQuizzesWithActivityUrl
  );
  saveQuizzesToCache = async (instituteId, courseId, quizzesToSave) => handleApiError(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/lms-course/${courseId}/save-quiz-cache?instituteId=${instituteId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          results: quizzesToSave
        })
      }
    )
  );
  getAssessmentQuestionsFromLMS = async (assessmentId) => handleApiErrorWithSchema(
    super.fetch(`${this.config.verityOrigin}/api/verity/assessment/${assessmentId}/lms-questions`, {
      credentials: "include"
    }),
    this.schemaValidators.assessmentQuestions
  );
  getAssessmentQuestions = async (assessmentId, includeDeleted) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/assessment/${assessmentId}/questions` + (includeDeleted ? "?includeDeleted=true" : ""),
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.assessmentQuestions
  );
  getQuestionSourcesWithEmail = async (assessmentId, questionId, fetchNewSources) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/dmca/${assessmentId}/questions/${questionId}/sources?fetch=${fetchNewSources}`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.questionSources
  );
  createUserQuestion = async (assessmentId, userQuestion) => handleApiErrorWithSchema(
    super.fetch(`${this.config.verityOrigin}/api/verity/assessment/${assessmentId}/user-question`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ question: userQuestion })
    }),
    this.schemaValidators.assessmentQuestion
  );
  createDecoySite = async (assessmentId) => handleApiError(
    super.fetch(`${this.config.verityOrigin}/api/verity/assessment/${assessmentId}/decoy-site`, {
      method: "PUT",
      credentials: "include"
    })
  );
  deleteDecoySite = async (assessmentId) => handleApiError(
    super.fetch(`${this.config.verityOrigin}/api/verity/assessment/${assessmentId}/decoy-site`, {
      method: "DELETE",
      credentials: "include"
    })
  );
  getLmsAssessmentInfo = async (assessmentId, courseId) => handleApiErrorWithSchema(
    super.fetch(`${this.config.verityOrigin}/api/verity/lms-course/${courseId}/lms-assessment/${assessmentId}/info`, {
      credentials: "include"
    }),
    this.schemaValidators.lmsAssessmentInfo
  );
  getVerityAssessment = async (assessmentId) => handleApiErrorWithSchema(
    super.fetch(`${this.config.verityOrigin}/api/verity/assessment/${assessmentId}/verity-assessment`, {
      credentials: "include"
    }),
    this.schemaValidators.verityAssessment
  );
  getVerityAssessmentInstructor = async (assessmentId) => handleApiErrorWithSchema(
    super.fetch(`${this.config.verityOrigin}/api/verity/assessment/${assessmentId}/verity-assessment/instructor`, {
      credentials: "include"
    }),
    this.schemaValidators.verityAssessment
  );
  getVerityAssessmentByLmsQuizId = async (lmsProvidedAssessmentId, lmsProvidedCourseId) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/lms-course/${lmsProvidedCourseId}/lms-assessment/${lmsProvidedAssessmentId}/verity-assessment`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.verityAssessment
  );
};

// src/support/index.ts
import { supportSchemaValidators as schemaValidators33 } from "@yujacorp/verity-api-contract/schemas/typebox";
var SupportClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators33);
  }
  sendSupportEmail = async ({ name, email, subject, message }) => {
    try {
      const res = await super.fetch(new URL(`/api/verity/support/email`, this.config.verityOrigin), {
        method: "POST",
        body: JSON.stringify({ name, email, subject, message }),
        headers: {
          "Content-Type": "application/json"
        }
      });
      if (res.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  sendPlatformRequestEmailCSM = async ({ name, link }) => {
    try {
      const res = await super.fetch(
        new URL(`/api/verity/support/send-platform-request-email-csm`, this.config.verityOrigin),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            name,
            link
          })
        }
      );
      return res.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCallAccessCode = async () => {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/support/getCallAccessCode`, {
        method: "GET",
        credentials: "include"
      }),
      this.schemaValidators.callAccessCode
    );
  };
};

// src/installation/index.ts
import { installationSchemaValidators as schemaValidators34 } from "@yujacorp/verity-api-contract/schemas/typebox";
var InstallationClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators34);
  }
  async createInstallation(systemInfo) {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/installation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(systemInfo)
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.installation.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async updateInstallation(id, systemInfo) {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/installation/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(systemInfo)
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.installation.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
};

// src/oauth/index.ts
import { oauthSchemaValidators as schemaValidators35 } from "@yujacorp/verity-api-contract/schemas/typebox";
var OAuthClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators35);
  }
  getAuthorizationUrl = async ({ courseId, callbackUrl }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/oauth/grant`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ courseId, callbackUrl })
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.authorization.validate(obj)) {
          return obj.authorizationUrl;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getOAuthAccessToken = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/oauth/token`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.accessToken.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  refreshStoredToken = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/oauth/refresh`, {
        credentials: "include"
      });
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/lti/index.ts
import { ltiSchemaValidators as schemaValidators36 } from "@yujacorp/verity-api-contract/schemas/typebox";
var LtiClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators36);
  }
  getInstituteClientRegistrations = async (instituteId) => handleApiErrorWithSchema(
    super.fetch(`${this.config.verityOrigin}/api/access-control/client-registration?instituteId=${instituteId}`),
    this.schemaValidators.clientRegistrations
  );
  updateClientRegistration = async ({
    id,
    linkageAttribute,
    linkUsersByEmail
  }) => handleApiErrorWithSchema(
    super.fetch(`${this.config.verityOrigin}/api/access-control/client-registration/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        linkageAttribute: linkageAttribute === void 0 ? null : linkageAttribute ?? "",
        linkUsersByEmail
      })
    }),
    this.schemaValidators.clientRegistration
  );
};

// src/basic-auth/index.ts
import { basicAuthSchemaValidators as schemaValidators37 } from "@yujacorp/verity-api-contract/schemas/typebox";
var BasicAuthClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators37);
  }
  login = async ({ email, password }) => {
    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      formData.append("password", password);
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/login`, {
        credentials: "include",
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: formData.toString()
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.login.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  createAccount = async ({ email, password }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/basic-auth`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password
        })
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.createAccount.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  sendVerificationEmail = async ({ email }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/basic-auth/email-verification`,
        {
          credentials: "include",
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email
          })
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  verifyEmail = async ({
    accountId,
    emailVerificationToken
  }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/basic-auth/verify-email`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountId,
          emailVerificationToken
        })
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.verifyEmail.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  sendResetPasswordEmail = async ({ email }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/basic-auth/reset-password`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email
        })
      });
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  resetPassword = async ({
    accountId,
    passwordRestToken,
    password
  }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/basic-auth/password`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountId,
          passwordRestToken,
          password
        })
      });
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  verifyTwoFactor = async ({ challengeToken, mfaCode }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/basic-auth/two-factor/verify`,
        {
          credentials: "include",
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            challengeToken,
            code: mfaCode
          })
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.verifyTwoFactor.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getTwoFactorStatus = async ({ accountId, accessToken }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/basic-auth/${accountId}/two-factor/status`,
        {
          credentials: "include",
          method: "GET",
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : void 0
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.twoFactorStatus.validate(obj)) {
          return obj.twoFactorVerified;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  disableTwoFactor = async ({ accountId, accessToken }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/basic-auth/${accountId}/two-factor`,
        {
          credentials: "include",
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/saml/index.ts
import { samlSchemaValidators as schemaValidators38 } from "@yujacorp/verity-api-contract/schemas/typebox";
var SAMLClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators38);
  }
  createRelyingPartyRegistration = async ({
    createRequestBody
  }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/relyingPartyRegistration`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...createRequestBody
        })
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getRelyingPartyRegistration = async ({ registrationId }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}`,
        {
          credentials: "include",
          method: "GET"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getRelyingPartyRegistrations = async ({ instituteId, enabled }) => {
    try {
      const searchParams = new URLSearchParams();
      searchParams.set("instituteId", instituteId);
      if (enabled !== void 0) {
        searchParams.set("enabled", enabled.toString());
      }
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/relyingPartyRegistration?${searchParams.toString()}`,
        {
          credentials: "include",
          method: "GET"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.getRelyingPartyRegistrations.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getRelyingPartyRegistrationIds = async ({ instituteId, enabled }) => {
    const searchParams = new URLSearchParams();
    searchParams.set("instituteId", instituteId);
    if (enabled !== void 0) {
      searchParams.set("enabled", enabled.toString());
    }
    searchParams.set("getRegistrationIds", "true");
    return handleApiErrorWithSchema(
      super.fetch(
        `${this.config.verityOrigin}/api/access-control/relyingPartyRegistration?${searchParams.toString()}`,
        {
          credentials: "include",
          method: "GET"
        }
      ),
      this.schemaValidators.getRelyingPartyRegistrationIds
    );
  };
  updateRelyingPartyRegistration = async ({
    registrationId,
    updateRequestBody
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}`,
        {
          credentials: "include",
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...updateRequestBody
          })
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  addRoleMapping = async ({
    registrationId,
    attribute,
    privilegeGroupId,
    requiredParentPrivilegeGroupId
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}/roleMapping`,
        {
          credentials: "include",
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attribute,
            privilegeGroupId,
            requiredParentPrivilegeGroupId
          })
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  deleteRoleMapping = async ({ registrationId, roleMappingId }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}/roleMapping/${roleMappingId}`,
        {
          credentials: "include",
          method: "DELETE",
          headers: { "Content-Type": "application/json" }
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getRoleMappingPresets = async ({ registrationId }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}/presets`,
        {
          credentials: "include",
          method: "GET",
          headers: { "Content-Type": "application/json" }
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.getRoleMappingPresets.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/media/index.ts
import { mediaSchemaValidators as schemaValidators39 } from "@yujacorp/verity-api-contract/schemas/typebox";
var MediaClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators39);
  }
  getNextRecordingNumbers = async ({ sessionId }) => {
    const searchParams = new URLSearchParams([
      ["number", "webcam"],
      ["number", "screencapture"],
      ["next", "true"]
    ]);
    const data = await handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/recording/${sessionId}?${searchParams.toString()}`),
      schemaValidators39.getNextRecordingNumbers
    );
    return {
      internal: data.screencapture,
      external: data.webcam
    };
  };
  uploadRecording = async ({
    sessionId,
    recordingDetails
  }) => {
    const videoBlob = new Blob([await fetch(recordingDetails.videoObjectUrl).then((r) => r.arrayBuffer())], {
      type: recordingDetails.videoMimeType
    });
    const searchParams = new URLSearchParams({
      segment: `${recordingDetails.blobNumber}`,
      url: "upload"
    });
    const response = await handleApiError(
      super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/recording/${sessionId}/${recordingDetails.recordingType === "display" ? "screencapture" : recordingDetails.recordingType}/${recordingDetails.videoNumber}?${searchParams.toString()}`
      )
    );
    if (response.status === 409) {
      throw new Error("409");
    } else if (!response.ok) {
      throw new ApiClientError(new Error(`Unable to get upload URL: ${response.status} - ${response.statusText}`));
    }
    const data = await response.json();
    if (!schemaValidators39.uploadRecording.validate(data)) {
      throw new ApiClientError(new Error(`Invalid upload URL: ${JSON.stringify(data)}`));
    }
    const s3Response = await fetch(data.url, {
      method: "PUT",
      body: videoBlob
    });
    if (!s3Response.ok) {
      throw new ApiClientError(new Error(`Upload to S3 failed: ${s3Response.status} - ${s3Response.statusText}`));
    }
  };
  uploadPhoto = async ({
    sessionId,
    type,
    imageObjectUrl,
    mimeType
  }) => {
    const imageBlob = new Blob([await fetch(imageObjectUrl).then((r) => r.arrayBuffer())], { type: mimeType });
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/photo/${sessionId}-${type}.png`, {
        method: "PUT",
        body: imageBlob
      })
    );
  };
};

// src/data-dump/index.ts
import { coerceToContractAbsoluteBase as coerceToContractAbsoluteBase3, DataDumpCallbackContract, DataDumpGrantContract, DataDumpLinksContract, withContractBase as withContractBase3 } from "@yujacorp/verity-api-contract";
var DataDumpClient = class extends ApiClient {
  constructor(config) {
    super(config, {});
  }
  createUploadGrant = withDefaultErrorHandler(async (dynamicInput) => {
    const handlers = withContractBase3({
      contract: DataDumpGrantContract,
      base: coerceToContractAbsoluteBase3(`${this.config.verityOrigin}/api/verity/`)
    }).withFetchParams({
      pathParams: dynamicInput.pathParams,
      body: dynamicInput.body
    }).withFetcher(async ({ method, url, body }) => {
      const res = await this.fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : void 0
      });
      return res.json();
    });
    const data = await handlers.toParsed();
    return data;
  });
  executeUploadCallback = withDefaultErrorHandler(async (dynamicInput) => {
    const handlers = withContractBase3({
      contract: DataDumpCallbackContract,
      base: coerceToContractAbsoluteBase3(`${this.config.verityOrigin}/api/verity/`)
    }).withFetchParams({
      pathParams: dynamicInput.pathParams,
      body: dynamicInput.body
    }).withFetcher(async ({ method, url, body }) => {
      const res = await this.fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : void 0
      });
      return { ok: res.ok };
    });
    const { ok } = await handlers.toRaw();
    return ok;
  });
  getLinks = withDefaultErrorHandler(async (dynamicInput) => {
    const handlers = withContractBase3({
      contract: DataDumpLinksContract,
      base: coerceToContractAbsoluteBase3(`${this.config.verityOrigin}/api/verity/`)
    }).withFetchParams({
      pathParams: dynamicInput.pathParams
    }).withFetcher(async ({ method, url }) => {
      const res = await this.fetch(url, {
        method,
        headers: { "Content-Type": "application/json" }
      });
      return res.json();
    });
    return handlers.toParsed();
  });
};

// src/payment/index.ts
var PaymentClient = class extends ApiClient {
  constructor(config) {
    super(config, {});
  }
  initiatePayment = async ({ assessmentId }) => {
    try {
      const res = await super.fetch(new URL(`/api/verity/payment`, this.config.verityOrigin), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          assessmentId
        })
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  checkPaymentStatus = async ({ sessionId, assessmentId }) => {
    try {
      const res = await super.fetch(
        new URL(
          `/api/verity/payment/complete?stripeSessionId=${sessionId}&assessmentId=${assessmentId}`,
          this.config.verityOrigin
        ),
        {
          method: "GET",
          credentials: "include"
        }
      );
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getEnabled = async () => {
    try {
      const res = await super.fetch(new URL(`/api/verity/payment/enabled`, this.config.verityOrigin), {
        method: "GET",
        credentials: "include"
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/feedback/index.ts
import { feedbackSchemaValidators as schemaValidators40 } from "@yujacorp/verity-api-contract/schemas/typebox";
var FeedbackClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators40);
  }
  submitFeedback = async ({
    satisfaction,
    category,
    summary,
    details
  }) => {
    try {
      const url = new URL("/api/verity/feedback", this.config.verityOrigin);
      const response = await super.fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          satisfaction,
          category,
          summary,
          details
        })
      });
      if (response.ok) return;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/scratch-note/index.ts
import { scratchNoteSchemaValidators as schemaValidators41 } from "@yujacorp/verity-api-contract/schemas/typebox";
var ScratchNoteClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators41);
  }
  createUploadGrant = async ({
    proctoringSessionUuid,
    mimeType
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/scratch-note/upload/grant/${proctoringSessionUuid}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ mimeType })
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.createUploadGrant.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  uploadCallback = async ({ scratchNoteId }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/scratch-note/upload/callback/${scratchNoteId}`,
        {
          method: "GET"
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getLinks = async ({ proctoringSessionUuid }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/scratch-note/links/${proctoringSessionUuid}`,
        {
          method: "GET"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.getLinks.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/report/index.ts
import { reportSchemaValidators as schemaValidators42 } from "@yujacorp/verity-api-contract/schemas/typebox";
var InstituteClient2 = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators42);
  }
  getInstituteReport = async ({
    instituteId,
    subAccountId,
    startDate,
    endDate,
    monthYearRange
  }) => {
    try {
      const params = new URLSearchParams({
        instituteId: instituteId.toString(),
        ...subAccountId ? { subAccountId: subAccountId.toString() } : {},
        startDate: startDate.getTime().toString(),
        endDate: endDate.getTime().toString(),
        monthYearRange: JSON.stringify(monthYearRange),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      const url = new URL(`/api/verity/report/admin/institute-report`, this.config.verityOrigin);
      url.search = params.toString();
      const res = await super.fetch(url);
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getSubAccounts = async () => {
    try {
      const url = new URL(`/api/verity/report/admin/subaccounts`, this.config.verityOrigin);
      const res = await super.fetch(url);
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getReceiveInstitutionReportEmail = async () => {
    try {
      const url = new URL(`/api/verity/report/admin/receive-institution-report-email`, this.config.verityOrigin);
      const res = await super.fetch(url);
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getUsageEmailsData = async ({
    instituteId,
    startDate,
    endDate,
    isCSM
  }) => {
    try {
      const params = new URLSearchParams({
        instituteId: instituteId.toString(),
        startMonth: startDate.month.toString(),
        startYear: startDate.year.toString(),
        endMonth: endDate.month.toString(),
        endYear: endDate.year.toString()
      });
      const url = new URL(`/api/verity/report/admin/get-monthly-usage-emails-data`, this.config.verityOrigin);
      url.search = params.toString();
      const res = await super.fetch(url);
      if (res.ok) {
        const raw = await res.json();
        const data = raw;
        if (!isCSM) {
          return data.filter((item) => !item.topMetrics.some((m) => m.type.toLowerCase().includes("csm")));
        }
        return data;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  generateHistoricEmailReport = async (email, canEditVeritySettings) => {
    try {
      const res = await super.fetch(`/api/verity/report/admin/generate-historic-email-report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          isForCSM: canEditVeritySettings
        })
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  generateEmailReportPDF = async (html) => {
    try {
      const res = await super.fetch(`/api/verity/report/admin/generate-email-report-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ html })
      });
      if (res.ok) {
        return await res.blob();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  updateReceiveInstitutionReportEmail = async (receiveEmail) => {
    try {
      const url = new URL(`/api/verity/report/admin/receive-institution-report-email`, this.config.verityOrigin);
      const res = await super.fetch(url, {
        method: "POST",
        body: JSON.stringify({ receiveEmail }),
        headers: { "Content-Type": "application/json" }
      });
      if (res.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  sendReportEmail = async (exportReportEmail) => {
    try {
      const { toEmail, reportType, filters, fileName, fileContent, contentType } = exportReportEmail;
      const res = await super.fetch(
        new URL(`/api/verity/report/admin/send-export-report-email`, this.config.verityOrigin),
        {
          method: "POST",
          body: JSON.stringify({
            toEmail,
            reportType,
            filters,
            fileName,
            fileContent,
            contentType
          }),
          headers: {
            "Content-Type": "application/json"
          }
        }
      );
      if (res.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/roster/index.ts
import { rosterSchemaValidators as schemaValidators43 } from "@yujacorp/verity-api-contract/schemas/typebox";
var RosterClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators43);
  }
  getRosterStatistics = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/roster/stats`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.getRosterStatistics.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getPrivilegeGroupIdsForRole = async (instituteUuid, role) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/access-control/roster/${instituteUuid}/role/${role}/privilege-group`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.privilegeGroupIds
  );
};

// src/theme/index.ts
var ThemeClient = class extends ApiClient {
  constructor(config) {
    super(config, {});
  }
  getVisualizerInfo = async (lms, instituteId) => {
    try {
      let downloadUrl;
      let viewUrl;
      if (lms === "canvas") {
        downloadUrl = `${this.config.verityOrigin}/api/verity/theme/canvas.js?instituteId=${instituteId}&download=true`;
        viewUrl = `${this.config.verityOrigin}/api/verity/theme/canvas.js?instituteId=${instituteId}`;
      } else if (lms === "desire2learn") {
        downloadUrl = `${this.config.verityOrigin}/api/verity/theme/d2l.js?instituteId=${instituteId}&download=true`;
        viewUrl = `${this.config.verityOrigin}/api/verity/theme/d2l.js?instituteId=${instituteId}`;
      } else if (lms === "moodle" && this.config?.visualizerOrigin) {
        downloadUrl = `${this.config.visualizerOrigin}/verity-local.zip`;
      } else {
        return void 0;
      }
      let response;
      if (lms === "moodle") {
        response = await fetch(new URL(downloadUrl), {
          // Do not use this.fetch
          method: "HEAD",
          headers: { Accept: "application/zip" }
        });
      } else {
        response = await this.fetch(new URL(downloadUrl), {
          method: "HEAD"
        });
      }
      if (!response.ok) throw new Error(`Failed to fetch visualizer metadata for ${lms}`);
      const contentLength = response.headers.get("Content-Length");
      const size = contentLength ? Number(contentLength) : lms === "moodle" ? void 0 : 1e3;
      return {
        downloadUrl,
        viewUrl,
        size,
        type: response.headers.get("Content-Type") || void 0
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/integration/index.ts
import { integrationSchemaValidators as schemaValidators44 } from "@yujacorp/verity-api-contract/schemas/typebox";
var IntegrationClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators44);
  }
  getVisualizerStatus = async (instituteId) => handleApiErrorWithSchema(
    super.fetch(
      new URL(`/api/verity/theme/${instituteId}/status`, this.config.verityOrigin),
      { credentials: "include" }
    ),
    this.schemaValidators.integrationStatus
  );
  getLti1p3Status = async (instituteId) => handleApiErrorWithSchema(
    super.fetch(
      new URL(`/api/verity/lti/1.3/${instituteId}/status`, this.config.verityOrigin),
      { credentials: "include" }
    ),
    this.schemaValidators.integrationStatus
  );
};

// src/trpc/index.ts
import { createTRPCClient, httpLink } from "@trpc/client";
import superjson from "superjson";
var TrpcClient = class extends ApiClient {
  trpc;
  constructor(config) {
    super(config, {});
    this.trpc = createTRPCClient({
      links: [
        httpLink({
          url: `${this.config.verityOrigin}/api/verity/trpc`,
          fetch: this.fetch.bind(this),
          transformer: superjson
        })
      ]
    });
  }
};

// src/index.ts
import {
  authenticationSchemaValidators as authenticationValidators,
  lmsInfoSchemaValidators as lmsInfoValidators,
  proctoringSessionSchemaValidators as proctoringSessionValidators,
  liveSchemaValidators as liveValidators,
  platformInfoSchemaValidators as platformInfoValidators,
  permissionSchemaValidators as permissionValidators,
  downloadSchemaValidators as downloadValidators,
  instituteSchemaValidators as instituteValidators,
  courseSchemaValidators as courseValidators,
  assessmentSchemaValidators as assessmentValidators,
  verityOptionsSchemaValidators as verityOptionsValidators,
  userSchemaValidators as userValidators,
  startAssessmentSchemaValidators as startAssessmentValidators,
  systemCheckSchemaValidators as systemCheckValidators,
  proctoringConfigurationSchemaValidators as proctoringConfigurationValidators,
  proctoringProfileSchemaValidators as proctoringProfileValidators,
  supportSchemaValidators as supportValidators,
  installationSchemaValidators as installationValidators,
  oauthSchemaValidators as oauthValidators,
  scratchNoteSchemaValidators as scratchNoteValidators,
  reportSchemaValidators as reportValidators,
  rosterSchemaValidators as rosterValidators,
  ltiSchemaValidators as ltiValidators,
  basicAuthSchemaValidators as basicAuthValidators,
  samlSchemaValidators as samlValidators,
  mediaSchemaValidators as mediaValidators,
  paymentSchemaValidators as stripeValidators,
  adminSchemaValidators as adminValidators,
  feedbackSchemaValidators as feedbackValidators,
  integrationSchemaValidators as integrationValidators
} from "@yujacorp/verity-api-contract/schemas/typebox";

// src/storage-adapters/local-storage.ts
var STORAGE_KEYS = {
  ACCESS_TOKEN: "verityToken",
  ACCESS_EXPIRES_AT: "verityAccessExpiresAt",
  REFRESH_TOKEN: "verityInfo",
  REFRESH_EXPIRES_AT: "verityRefreshExpiresAt",
  ACCOUNT_ID: "verityAccountId",
  LOCAL_REFRESHED_TIME: "verityLocalRefreshTimestamp"
};
function createLocalStorageAdapter(config) {
  return {
    ...config,
    /**
     * Retrieves the refresh token from localStorage.
     * @returns The stored refresh token or null if not found
     */
    getRefreshToken: async () => {
      return Promise.resolve(localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ?? void 0);
    },
    /**
     * Stores or removes the refresh token in localStorage.
     * If the token is undefined, removes the key entirely.
     * @param refreshToken - The token to store or null to remove
     */
    setRefreshToken: async (refreshToken) => {
      if (refreshToken === void 0) {
        return Promise.resolve(localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN));
      } else {
        return Promise.resolve(localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken));
      }
    },
    /**
     * Retrieves the account ID from localStorage.
     * @returns The stored account ID or null if not found
     */
    getAccountId: async () => {
      return Promise.resolve(localStorage.getItem(STORAGE_KEYS.ACCOUNT_ID) ?? void 0);
    },
    /**
     * Stores or removes the account ID in localStorage.
     * If the ID is falsy, removes the key entirely.
     * @param accountId - The ID to store or null to remove
     */
    setAccountId: async (accountId) => {
      if (accountId) {
        return Promise.resolve(localStorage.setItem(STORAGE_KEYS.ACCOUNT_ID, accountId));
      } else {
        return Promise.resolve(localStorage.removeItem(STORAGE_KEYS.ACCOUNT_ID));
      }
    },
    /**
     * Retrieves the access token from localStorage.
     * @returns The stored access token or null if not found
     */
    getAccessToken: async () => {
      return Promise.resolve(localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ?? void 0);
    },
    /**
     * Stores or removes the access token in localStorage.
     * If the token is undefined, removes the key entirely.
     * @param accessToken - The token to store or null to remove
     */
    setAccessToken: async (accessToken) => {
      if (accessToken) {
        return Promise.resolve(localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken));
      } else {
        return Promise.resolve(localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN));
      }
    },
    /**
     * Retrieves the local refresh timestamp from localStorage.
     * Converts the stored string value to a number.
     * @returns The timestamp as a number or null if not found
     */
    getLocalRefreshedTime: async () => {
      const timestamp = localStorage.getItem(STORAGE_KEYS.LOCAL_REFRESHED_TIME);
      return Promise.resolve(timestamp ? Number.parseInt(timestamp) : void 0);
    },
    /**
     * Stores or removes the local refresh timestamp in localStorage.
     * Converts the number to a string for storage.
     * @param timestamp - The timestamp to store or null to remove
     */
    setLocalRefreshedTime: async (timestamp) => {
      if (timestamp) {
        return Promise.resolve(localStorage.setItem(STORAGE_KEYS.LOCAL_REFRESHED_TIME, timestamp.toString()));
      } else {
        return Promise.resolve(localStorage.removeItem(STORAGE_KEYS.LOCAL_REFRESHED_TIME));
      }
    },
    /**
     * Verifies if localStorage is available and functioning in the current environment.
     *
     * Performs the following checks:
     * 1. Verifies that window.localStorage exists
     * 2. Tests writing and reading from localStorage
     * 3. Handles any permission or access errors
     *
     * @returns A boolean indicating if localStorage is supported
     */
    isEnvironmentSupported: () => {
      try {
        if (typeof window === "undefined" || !window.localStorage) {
          return false;
        }
        const testKey = "__test_storage__";
        localStorage.setItem(testKey, "test");
        const testValue = localStorage.getItem(testKey);
        localStorage.removeItem(testKey);
        if (testValue !== "test") {
          return false;
        }
        return true;
      } catch {
        return false;
      }
    }
  };
}

// src/storage-adapters/lti-postmessage.ts
var DEFAULT_TIMEOUT = 5e3;
var STORAGE_KEYS2 = {
  /** Access token for API authentication */
  ACCESS_TOKEN: "lti.verity.accessToken",
  /** Account identifier for the current user */
  ACCOUNT_ID: "lti.verity.accountId",
  /** Refresh token for obtaining new access tokens */
  REFRESH_TOKEN: "lti.verity.refreshToken",
  /** Timestamp of the last token refresh operation */
  LOCAL_REFRESHED_TIME: "lti.verity.localRefreshTime"
};
function generateMessageId() {
  return `lti_msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}
async function sendMessage(targetWindow, targetOrigin, message, timeout) {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      window.removeEventListener("message", messageHandler);
      resolve(void 0);
    }, timeout);
    function messageHandler(event) {
      const isValidOrigin = event.origin === targetOrigin;
      if (isValidOrigin) {
        const data = event.data;
        if (data !== null) {
          const isValidResponseStructure = typeof data === "object" && "subject" in data && typeof data.subject === "string" && "message_id" in data && typeof data.message_id === "string";
          if (!isValidOrigin || !isValidResponseStructure) {
            return;
          }
          window.removeEventListener("message", messageHandler);
          clearTimeout(timeoutId);
          const verifiedData = data;
          if (verifiedData.error) {
            resolve(void 0);
          } else {
            resolve(verifiedData.value);
          }
        }
      }
    }
    window.addEventListener("message", messageHandler);
    targetWindow.postMessage(message, targetOrigin);
  });
}
function createLtiPostMessageAdapter(targetWindow, targetOrigin, config, timeout = DEFAULT_TIMEOUT) {
  return {
    ...config,
    getRefreshToken: async () => {
      return sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.get_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.REFRESH_TOKEN
        },
        timeout
      );
    },
    setRefreshToken: async (refreshToken) => {
      await sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.put_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.REFRESH_TOKEN,
          value: refreshToken
        },
        timeout
      );
    },
    getAccountId: async () => {
      return sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.get_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.ACCOUNT_ID
        },
        timeout
      );
    },
    setAccountId: async (accountId) => {
      await sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.put_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.ACCOUNT_ID,
          value: accountId
        },
        timeout
      );
    },
    getAccessToken: async () => {
      return sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.get_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.ACCESS_TOKEN
        },
        timeout
      );
    },
    setAccessToken: async (accessToken) => {
      await sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.put_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.ACCESS_TOKEN,
          value: accessToken
        },
        timeout
      );
    },
    getLocalRefreshedTime: async () => {
      return sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.get_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.LOCAL_REFRESHED_TIME
        },
        timeout
      );
    },
    setLocalRefreshedTime: async (timestamp) => {
      await sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.put_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.LOCAL_REFRESHED_TIME,
          value: timestamp
        },
        timeout
      );
    },
    isEnvironmentSupported: () => {
      try {
        if (typeof window === "undefined") {
          return false;
        }
        if (!targetWindow || targetWindow.closed) {
          return false;
        }
        if (!targetWindow.postMessage) {
          return false;
        }
        return true;
      } catch (error) {
        return false;
      }
    }
  };
}

// src/storage-adapters/state.ts
function createStateStorageAdapter(state, config) {
  return {
    ...config,
    /**
     * Retrieves the refresh token from the state
     * @returns The stored refresh token or null if not found
     */
    getRefreshToken: async () => {
      return await state.use((_, { refreshToken }) => refreshToken);
    },
    /**
     * Stores the refresh token in the state
     * @param refreshToken - The token to store or null to clear
     */
    setRefreshToken: async (refreshToken) => {
      return await state.use((_, persistentState) => {
        persistentState.refreshToken = refreshToken;
        return;
      });
    },
    /**
     * Retrieves the account ID from the state
     * @returns The stored account ID or null if not found
     */
    getAccountId: async () => {
      return await state.use((_, { accountId }) => accountId);
    },
    /**
     * Stores the account ID in the state
     * @param accountId - The ID to store or null to clear
     */
    setAccountId: async (accountId) => {
      return await state.use((_, persistentState) => {
        persistentState.accountId = accountId;
        return;
      });
    },
    /**
     * Retrieves the access token from the state
     * @returns The stored access token or null if not found
     */
    getAccessToken: async () => {
      return await state.use((_, { accessToken }) => accessToken);
    },
    /**
     * Stores the access token in the state
     * @param accessToken - The token to store or null to clear
     */
    setAccessToken: async (accessToken) => {
      return await state.use((_, persistentState) => {
        persistentState.accessToken = accessToken;
        return;
      });
    },
    /**
     * Retrieves the local refresh timestamp from the state
     * @returns The timestamp or null if not found
     */
    getLocalRefreshedTime: async () => {
      return await state.use((_, { localRefreshedTime }) => localRefreshedTime);
    },
    /**
     * Stores the local refresh timestamp in the state
     * @param timestamp - The timestamp to store or null to clear
     */
    setLocalRefreshedTime: async (timestamp) => {
      return await state.use((_, persistentState) => {
        persistentState.localRefreshedTime = timestamp;
        return;
      });
    },
    /**
     * Verifies if the state manager can support token storage operations.
     *
     * Checks:
     * 1. If state manager is accessible and supports use callback
     *
     * @returns A boolean indicating if the state manager is supported
     */
    isEnvironmentSupported: () => {
      try {
        if (typeof state === "undefined") {
          return false;
        }
        if (typeof state.use !== "function") {
          return false;
        }
        return true;
      } catch {
        return false;
      }
    }
  };
}

// src/admin/index.ts
import {
  adminSchemaValidators as schemaValidators45
} from "@yujacorp/verity-api-contract/schemas/typebox";
var AdminClient = class extends ApiClient {
  constructor(config) {
    super(config, schemaValidators45);
  }
  updateVerityInstituteSettings = async (instituteId, settings) => {
    return await handleApiError(
      super.fetch(new URL(`/api/verity/lti-institute/${instituteId}/settings`, this.config.verityOrigin), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
        credentials: "include"
      })
    );
  };
  updateDeploymentAndIssuer = async (instituteId, data) => handleApiError(
    super.fetch(
      new URL(`/api/verity/lti-institute/${instituteId}/deployment`, this.config.verityOrigin),
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data)
      }
    )
  );
  #getActiveVerityDeployment = async (instituteId) => handleApiErrorWithSchema(
    super.fetch(
      new URL(`/api/verity/institute/${instituteId}/deployment`, this.config.verityOrigin),
      { credentials: "include" }
    ),
    this.schemaValidators.activeVerityDeployment
  );
  #getDeployments = async (instituteUuid) => handleApiErrorWithSchema(
    super.fetch(
      new URL(`/api/access-control/institute/${instituteUuid}/deployment`, this.config.verityOrigin),
      { credentials: "include" }
    ),
    this.schemaValidators.deployments
  );
  #getClientRegistration = async (id) => handleApiErrorWithSchema(
    super.fetch(
      new URL(`/api/access-control/client-registration/${id}`, this.config.verityOrigin),
      { credentials: "include" }
    ),
    this.schemaValidators.clientRegistration
  );
  getActiveDeployment = async ({
    instituteId,
    instituteUuid
  }) => {
    try {
      const activeVerityDeployment = await this.#getActiveVerityDeployment(instituteId);
      if (!activeVerityDeployment || !activeVerityDeployment.deploymentId || !activeVerityDeployment.clientId || !activeVerityDeployment.iss) return void 0;
      const clientRegistrationId = (await this.getClientRegistrationFromClientIdAndIss(
        activeVerityDeployment.clientId,
        activeVerityDeployment.iss
      ))?.clientRegistrations?.[0]?.id;
      if (!clientRegistrationId) return void 0;
      const deployments = (await this.#getDeployments(instituteUuid)).deployments;
      if (!deployments || deployments.length === 0) return void 0;
      const activeDeployment = deployments.find(
        (deployment) => deployment.deploymentId === activeVerityDeployment.deploymentId && deployment.lti1p3ClientRegistrationId === clientRegistrationId
      );
      if (!activeDeployment) return void 0;
      const clientRegistration = await this.#getClientRegistration(activeDeployment.lti1p3ClientRegistrationId);
      if (!clientRegistration) return void 0;
      return {
        id: activeDeployment.id,
        lti1p3ClientRegistrationId: activeDeployment.lti1p3ClientRegistrationId,
        deploymentId: activeDeployment.deploymentId,
        clientId: clientRegistration.clientId,
        iss: clientRegistration.iss,
        authorizationUri: clientRegistration.authorizationUri,
        jwkSetUri: clientRegistration.jwkSetUri
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  createDeployment = async (instituteUuid, data) => handleApiErrorWithSchema(
    super.fetch(
      new URL(`/api/access-control/institute/${instituteUuid}/deployment`, this.config.verityOrigin),
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data)
      }
    ),
    this.schemaValidators.deployment
  );
  updateDeployment = async (id, instituteUuid, data) => handleApiErrorWithSchema(
    super.fetch(
      new URL(`/api/access-control/institute/${instituteUuid}/deployment/${id}`, this.config.verityOrigin),
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data)
      }
    ),
    this.schemaValidators.deployment
  );
  deleteDeployment = async (instituteUuid, id) => handleApiError(
    super.fetch(
      new URL(`/api/access-control/institute/${instituteUuid}/deployment/${id}`, this.config.verityOrigin),
      {
        method: "DELETE",
        credentials: "include"
      }
    )
  );
  getClientRegistrationFromClientIdAndIss = async (clientId, iss) => handleApiErrorWithSchema(
    super.fetch(
      new URL(`/api/access-control/client-registration?clientId=${encodeURIComponent(clientId)}&iss=${encodeURIComponent(iss)}`, this.config.verityOrigin),
      { credentials: "include" }
    ),
    this.schemaValidators.clientRegistrationId
  );
  upsertDeploymentClientRegistration = async (instituteUuid, deploymentId, data) => handleApiErrorWithSchema(
    super.fetch(
      new URL(`/api/access-control/institute/${instituteUuid}/deployment/${deploymentId}/client-registration`, this.config.verityOrigin),
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instituteId: instituteUuid, ...data })
      }
    ),
    this.schemaValidators.upsertedClientRegistrationId
  );
  createClientRegistration = async (instituteUuid, data) => handleApiErrorWithSchema(
    super.fetch(
      new URL("/api/access-control/client-registration", this.config.verityOrigin),
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instituteId: instituteUuid, ...data })
      }
    ),
    this.schemaValidators.clientRegistration
  );
};

// src/restricted-device/index.ts
import {
  RestrictedDeviceGrantContract,
  RestrictedDeviceRegistrationContract,
  withContractBase as withContractBase4,
  coerceToContractAbsoluteBase as coerceToContractAbsoluteBase4,
  RestrictedDeviceDeletePeerContract
} from "@yujacorp/verity-api-contract";
var RestrictedDeviceClient = class extends ApiClient {
  constructor(config) {
    super(config, {});
  }
  registerPeer = withDefaultErrorHandler(
    async (dynamicInput) => {
      const handlers = withContractBase4({
        contract: RestrictedDeviceRegistrationContract,
        base: coerceToContractAbsoluteBase4(`${this.config.verityOrigin}/api/verity/`)
      }).withFetchParams({
        body: dynamicInput.body
      }).withFetcher(async ({ method, url, body }) => {
        const res = await this.fetch(url, {
          method,
          headers: { "Content-Type": "application/json" },
          body: body ? JSON.stringify(body) : void 0
        });
        return res.json();
      });
      return handlers.toParsed();
    }
  );
  deletePeer = withDefaultErrorHandler(
    async (dynamicInput) => {
      const handlers = withContractBase4({
        contract: RestrictedDeviceDeletePeerContract,
        base: coerceToContractAbsoluteBase4(`${this.config.verityOrigin}/api/verity/`)
      }).withFetchParams({
        pathParams: dynamicInput.pathParams
      }).withFetcher(
        async ({ method, url }) => this.fetch(url, {
          method,
          headers: { "Content-Type": "application/json" }
        })
      );
      await handlers.toRaw();
    }
  );
  createSingleUseGrant = withDefaultErrorHandler(
    async (dynamicInput) => {
      const handlers = withContractBase4({
        contract: RestrictedDeviceGrantContract,
        base: coerceToContractAbsoluteBase4(`${this.config.verityOrigin}/api/verity/`)
      }).withFetchParams({
        pathParams: dynamicInput.pathParams
      }).withFetcher(async ({ method, url }) => {
        const res = await this.fetch(url, {
          method,
          headers: { "Content-Type": "application/json" }
        });
        return res.json();
      });
      return handlers.toParsed();
    }
  );
};

// src/index.ts
var VerityApiClient = class extends ApiClient {
  authentication;
  lmsInfo;
  proctoringSession;
  liveClient;
  platformInfo;
  permission;
  download;
  institute;
  course;
  assessment;
  verityOptions;
  user;
  startAssessmentClient;
  systemCheck;
  proctoringConfiguration;
  proctoringProfile;
  support;
  installation;
  oauth;
  scratchNote;
  report;
  roster;
  lti;
  saml;
  basicAuth;
  media;
  dataDump;
  payment;
  admin;
  restrictedDevice;
  theme;
  feedback;
  trpc;
  integration;
  constructor(config) {
    super(config, {});
    this.authentication = new AuthenticationClient(config);
    this.lmsInfo = new LmsInfoClient(config);
    this.proctoringSession = new ProctoringSessionClient(config);
    this.liveClient = new LiveClient(config);
    this.platformInfo = new PlatformInfoClient(config);
    this.permission = new PermissionClient(config);
    this.download = new DownloadClient(config);
    this.institute = new InstituteClient(config);
    this.course = new CourseClient(config);
    this.assessment = new AssessmentClient(config);
    this.verityOptions = new VerityOptionsClient(config);
    this.user = new UserClient(config);
    this.startAssessmentClient = new startAssessmentClient(config);
    this.systemCheck = new SystemCheckClient(config);
    this.proctoringConfiguration = new ProctoringConfigurationClient(config);
    this.proctoringProfile = new ProctoringProfileClient(config);
    this.support = new SupportClient(config);
    this.installation = new InstallationClient(config);
    this.oauth = new OAuthClient(config);
    this.scratchNote = new ScratchNoteClient(config);
    this.report = new InstituteClient2(config);
    this.roster = new RosterClient(config);
    this.lti = new LtiClient(config);
    this.saml = new SAMLClient(config);
    this.basicAuth = new BasicAuthClient(config);
    this.media = new MediaClient(config);
    this.dataDump = new DataDumpClient(config);
    this.payment = new PaymentClient(config);
    this.admin = new AdminClient(config);
    this.restrictedDevice = new RestrictedDeviceClient(config);
    this.theme = new ThemeClient(config);
    this.feedback = new FeedbackClient(config);
    this.trpc = new TrpcClient(config);
    this.integration = new IntegrationClient(config);
  }
};
var VerityApiValidator = class {
  authentication;
  lmsInfo;
  proctoringSession;
  liveClient;
  platformInfo;
  permission;
  download;
  institute;
  course;
  assessment;
  verityOptions;
  user;
  startAssessmentClient;
  systemCheck;
  proctoringConfiguration;
  proctoringProfile;
  support;
  installation;
  oauth;
  scratchNote;
  report;
  roster;
  lti;
  saml;
  basicAuth;
  media;
  stripe;
  admin;
  feedback;
  integration;
  constructor() {
    this.authentication = { schemaValidators: authenticationValidators };
    this.lmsInfo = { schemaValidators: lmsInfoValidators };
    this.proctoringSession = { schemaValidators: proctoringSessionValidators };
    this.liveClient = { schemaValidators: liveValidators };
    this.platformInfo = { schemaValidators: platformInfoValidators };
    this.permission = { schemaValidators: permissionValidators };
    this.download = { schemaValidators: downloadValidators };
    this.institute = { schemaValidators: instituteValidators };
    this.course = { schemaValidators: courseValidators };
    this.assessment = { schemaValidators: assessmentValidators };
    this.verityOptions = { schemaValidators: verityOptionsValidators };
    this.user = { schemaValidators: userValidators };
    this.startAssessmentClient = { schemaValidators: startAssessmentValidators };
    this.systemCheck = { schemaValidators: systemCheckValidators };
    this.proctoringConfiguration = { schemaValidators: proctoringConfigurationValidators };
    this.proctoringProfile = { schemaValidators: proctoringProfileValidators };
    this.support = { schemaValidators: supportValidators };
    this.installation = { schemaValidators: installationValidators };
    this.oauth = { schemaValidators: oauthValidators };
    this.scratchNote = { schemaValidators: scratchNoteValidators };
    this.report = { schemaValidators: reportValidators };
    this.roster = { schemaValidators: rosterValidators };
    this.lti = { schemaValidators: ltiValidators };
    this.saml = { schemaValidators: samlValidators };
    this.basicAuth = { schemaValidators: basicAuthValidators };
    this.media = { schemaValidators: mediaValidators };
    this.stripe = { schemaValidators: stripeValidators };
    this.admin = { schemaValidators: adminValidators };
    this.feedback = { schemaValidators: feedbackValidators };
    this.integration = { schemaValidators: integrationValidators };
  }
};
var WebApiClient = class _WebApiClient extends VerityApiClient {
  static instances = /* @__PURE__ */ new Map();
  constructor(config) {
    super(config);
  }
  /**
   * Generate a unique key for the config to use as map key
   * @param config Platform configuration
   * @returns A string key representing the config
   */
  static getConfigKey(config) {
    return `${config.verityOrigin}:${config.lockdown.origin}:${config.lockdown.channel}`;
  }
  /**
   * Get or create a singleton instance of WebApiClient for the given config
   * @param config Platform configuration that determines which instance to use
   * @returns The singleton instance of WebApiClient for the given config
   */
  static getInstance(config) {
    const configKey = _WebApiClient.getConfigKey(config);
    const existingInstance = _WebApiClient.instances.get(configKey);
    if (existingInstance) {
      return existingInstance;
    }
    const localStorageAdapter = createLocalStorageAdapter(config);
    if (localStorageAdapter.isEnvironmentSupported()) {
      const instance = new _WebApiClient(localStorageAdapter);
      _WebApiClient.instances.set(configKey, instance);
      return instance;
    }
    const ltiPostMessageAdapter = createLtiPostMessageAdapter(
      window.top ?? window.parent,
      new URL(window.location.ancestorOrigins[0]).origin,
      config
    );
    if (ltiPostMessageAdapter.isEnvironmentSupported()) {
      const instance = new _WebApiClient(ltiPostMessageAdapter);
      _WebApiClient.instances.set(configKey, instance);
      return instance;
    }
    throw new Error("No valid adapter found");
  }
};
var WorkerApiClient = class _WorkerApiClient extends VerityApiClient {
  static instances = /* @__PURE__ */ new Map();
  constructor(config) {
    super(config);
  }
  /**
   * Generate a unique key for the config to use as map key
   * @param config Platform configuration
   * @returns A string key representing the config
   */
  static getConfigKey(config) {
    return `${config.verityOrigin}:${config.lockdown.origin}:${config.lockdown.channel}`;
  }
  /**
   * Get or create a singleton instance of WorkerApiClient for the given config and state manager
   * @param state State manager to use for storage
   * @param config Platform configuration that determines which instance to use
   * @returns The singleton instance of WorkerApiClient for the given config and state
   */
  static getInstance(state, config) {
    const instanceKey = _WorkerApiClient.getConfigKey(config);
    const existingInstance = _WorkerApiClient.instances.get(instanceKey);
    if (existingInstance) {
      return existingInstance;
    }
    const stateStorageAdapter = createStateStorageAdapter(state, config);
    if (stateStorageAdapter.isEnvironmentSupported()) {
      const instance = new _WorkerApiClient(stateStorageAdapter);
      _WorkerApiClient.instances.set(instanceKey, instance);
      return instance;
    }
    throw new Error("No valid adapter found");
  }
};
export {
  ApiClientError,
  ApiClientResponseError,
  ApiClientSchemaError,
  BaseApiClientError,
  UnauthenticatedError,
  VerityApiClient,
  VerityApiValidator,
  WebApiClient,
  WorkerApiClient,
  handleApiError,
  handleApiErrorWithSchema,
  withDefaultErrorHandler
};
//# sourceMappingURL=index.js.map